import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '@/components/Header';
import AdminFinancialPanel from '@/components/AdminFinancialPanel';
import SharedAnalysesPanel from '@/components/SharedAnalysesPanel';
import EmailHistoryModal from '@/components/EmailHistoryModal';
import CuratedExamplesPanel from '@/components/CuratedExamplesPanel';
import UploadStorageHealth from '@/components/UploadStorageHealth';
import AdminFeatureVisibilityPanel from '@/components/AdminFeatureVisibilityPanel';
import SuspectedSplitsPanel from '@/components/SuspectedSplitsPanel';
import AdminMissingFishBaselinePanel from '@/components/AdminMissingFishBaselinePanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  ArrowLeft, 
  Users, 
  Activity, 
  BarChart3, 
  Lock, 
  Unlock, 
  Search,
  ArrowUpDown,
  Download,
  RefreshCw,
  Circle,
  Trash2,
  AlertTriangle,
  Ban,
  ShieldOff,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Eye,
  FileSpreadsheet,
  Upload,
  Settings2,
  Loader2,
  Thermometer,
  LineChart,
  AlertCircle,
  Copy,
  Star,
  Percent,
  Edit,
  RotateCcw,
  Check,
  X,
  Shield,
  Mail,
  Inbox,
  PlayCircle,
  KeyRound
} from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Protected emails that cannot be deleted (but can be blocked/unblocked except admin)
const PROTECTED_EMAILS = [
  'totalproductionsolutions@gmail.com',
  'chrisclayburn@aqua-ecosystems.com'
];

// Admin email cannot be blocked
const ADMIN_EMAIL = 'totalproductionsolutions@gmail.com';

export default function AdminConsole() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // Filter by quota status
  const [sortBy, setSortBy] = useState('last_seen_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Delete modals state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [deleteAllStep, setDeleteAllStep] = useState(1);
  const [deleting, setDeleting] = useState(false);

  // Model Master Control state
  const [modelOverrides, setModelOverrides] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState('Seabream');
  const [selectedGeneticOrigin, setSelectedGeneticOrigin] = useState('Greek');
  const [modelAdjustment, setModelAdjustment] = useState(100);  // Affects GROWTH only
  const [feedAdjustment, setFeedAdjustment] = useState(100);    // Affects FEED only
  const [showOverrideConfirmModal, setShowOverrideConfirmModal] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);

  // Custom Model Approval state
  const [customModels, setCustomModels] = useState([]);
  const [customModelsLoading, setCustomModelsLoading] = useState(false);
  const [customModelStatusFilter, setCustomModelStatusFilter] = useState('');
  const [showModelReviewModal, setShowModelReviewModal] = useState(false);
  const [modelToReview, setModelToReview] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [updatingModel, setUpdatingModel] = useState(false);
  const [customModelsExpanded, setCustomModelsExpanded] = useState(true);

  // Client Model Management state
  const [clientsWithModels, setClientsWithModels] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientModels, setSelectedClientModels] = useState([]);
  const [clientModelsLoading, setClientModelsLoading] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [clientModelsSectionExpanded, setClientModelsSectionExpanded] = useState(true);

  // Client Model Assignment state
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentClientId, setAssignmentClientId] = useState(null);
  const [assignmentClientEmail, setAssignmentClientEmail] = useState('');
  const [clientApprovedModels, setClientApprovedModels] = useState([]);
  const [assignmentForm, setAssignmentForm] = useState({
    sgr_model_source: 'DEFAULT',
    sgr_custom_model_id: '',
    fcr_model_source: 'DEFAULT',
    fcr_custom_model_id: ''
  });
  const [savingAssignment, setSavingAssignment] = useState(false);

  // Temperature Model Management state
  const [temperatureModels, setTemperatureModels] = useState([]);
  const [temperatureModelsLoading, setTemperatureModelsLoading] = useState(false);
  const [temperatureStatusFilter, setTemperatureStatusFilter] = useState('');
  const [temperatureModelsExpanded, setTemperatureModelsExpanded] = useState(true);
  const [showTempModelReviewModal, setShowTempModelReviewModal] = useState(false);
  const [tempModelToReview, setTempModelToReview] = useState(null);
  const [tempReviewStatus, setTempReviewStatus] = useState('');
  const [tempReviewNotes, setTempReviewNotes] = useState('');
  const [tempIsPublic, setTempIsPublic] = useState(false);
  const [updatingTempModel, setUpdatingTempModel] = useState(false);
  const [downloadingTempFileId, setDownloadingTempFileId] = useState(null);
  
  // Temperature Model Comparison state
  const [tempComparisonExpanded, setTempComparisonExpanded] = useState(true);
  const [installedProfiles, setInstalledProfiles] = useState([]);
  const [selectedCompareModels, setSelectedCompareModels] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Installed Models state
  const [installedModelsExpanded, setInstalledModelsExpanded] = useState(true);
  const [sgrModels, setSgrModels] = useState([]);
  const [sfrModels, setSfrModels] = useState([]);
  const [installedModelsLoading, setInstalledModelsLoading] = useState(false);
  const [downloadingModelId, setDownloadingModelId] = useState(null);
  
  // Profile Name Editing state
  const [profileNames, setProfileNames] = useState([]);
  const [profileNamesLoading, setProfileNamesLoading] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [editingProfileName, setEditingProfileName] = useState('');
  const [savingProfileName, setSavingProfileName] = useState(false);

  // FCR Origins Management state
  const [fcrOrigins, setFcrOrigins] = useState([]);
  const [fcrOriginsLoading, setFcrOriginsLoading] = useState(false);
  const [fcrOriginsExpanded, setFcrOriginsExpanded] = useState(true);

  // Security Dashboard state
  const [securityData, setSecurityData] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityExpanded, setSecurityExpanded] = useState(true);

  // Support Inbox state
  const [supportThreads, setSupportThreads] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportExpanded, setSupportExpanded] = useState(true);
  const [activeSupport, setActiveSupport] = useState(null);

  // ★ Onboarding Video Config (admin-controlled global setting)
  const [onboardingCfg, setOnboardingCfg] = useState({
    enabled: true,
    video_url: '',
    updated_by: null,
    updated_at: null,
    default_video_url: '',
  });
  const [onboardingCfgLoading, setOnboardingCfgLoading] = useState(false);
  const [onboardingCfgSaving, setOnboardingCfgSaving] = useState(false);
  const [onboardingCfgDirty, setOnboardingCfgDirty] = useState(false);
  const [onboardingCfgExpanded, setOnboardingCfgExpanded] = useState(true);

  // Normalize common YouTube URL forms to an embeddable form, client-side.
  // Mirrors the backend helper so the live <iframe> preview stays usable for
  // any URL flavor (youtu.be / watch?v= / /embed/). Non-YouTube URLs pass-through.
  const toEmbedUrl = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim();
    let m = s.match(/^https?:\/\/youtu\.be\/([A-Za-z0-9_-]{6,})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    m = s.match(/^https?:\/\/(?:www\.)?youtube\.com\/watch\?(?:.*&)?v=([A-Za-z0-9_-]{6,})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    return s;
  };
  const [supportReply, setSupportReply] = useState('');
  const [editingVersionId, setEditingVersionId] = useState(null);
  const [editingMultiplier, setEditingMultiplier] = useState(100);
  const [editingVersionName, setEditingVersionName] = useState('');
  const [savingVersion, setSavingVersion] = useState(false);
  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [addVersionOriginCode, setAddVersionOriginCode] = useState('');
  const [newVersionName, setNewVersionName] = useState('');
  const [newVersionMultiplier, setNewVersionMultiplier] = useState(100);
  const [newVersionNotes, setNewVersionNotes] = useState('');

  // Species that support genetic origin
  // Set of species that have a per-strain BFCR catalogue (genetic origin
  // applies). Today only Seabream — Tilapia/Seabass don't differentiate by
  // genetic strain, and Tripletail uses a static time-driven model.
  const speciesWithGeneticOrigin = ['Seabream'];
  const geneticOrigins = ['Greek', 'French', 'Italian'];
  // Species that admins can manage in the FCR / model overrides catalogues.
  // Salmon was removed pre-deployment — it has no model implementation
  // anywhere in the platform yet, so showing it in admin lists could
  // mislead operators. It will be added back once the Salmon SGR / BFCR
  // tables are wired in (see ROADMAP.md).
  const allSpecies = ['Seabream', 'Tilapia', 'Seabass'];

  // SECURITY: Strict admin access check - redirect immediately if not admin
  useEffect(() => {
    // Check both is_admin flag AND exact email match for defense in depth
    const isAdminUser = user?.is_admin === true && user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
    if (user && !isAdminUser) {
      toast.error('Admin access denied - unauthorized user');
      navigate('/');
    }
  }, [user, navigate]);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load metrics');
      }
    }
  }, [token, navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: searchQuery,
          sort_by: sortBy,
          sort_order: sortOrder,
          limit: 100
        }
      });
      setUsers(response.data.users);
      setTotalUsers(response.data.total_count);
      // Clear selection when users list changes
      setSelectedUsers(new Set());
      setSelectAll(false);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, [token, searchQuery, sortBy, sortOrder]);

  // Fetch model overrides
  const fetchModelOverrides = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/model-overrides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModelOverrides(response.data);
    } catch (error) {
      console.error('Failed to load model overrides:', error);
    }
  }, [token]);

  // Fetch custom models for admin review
  const fetchCustomModels = useCallback(async () => {
    setCustomModelsLoading(true);
    try {
      const params = customModelStatusFilter ? { status_filter: customModelStatusFilter } : {};
      const response = await axios.get(`${API}/admin/custom-models`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setCustomModels(response.data || []);
    } catch (error) {
      console.error('Failed to load custom models:', error);
    } finally {
      setCustomModelsLoading(false);
    }
  }, [token, customModelStatusFilter]);

  // Fetch clients with models
  const fetchClientsWithModels = useCallback(async () => {
    setClientsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/clients-with-models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientsWithModels(response.data || []);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setClientsLoading(false);
    }
  }, [token]);

  // Fetch temperature models for admin review
  const fetchTemperatureModels = useCallback(async () => {
    setTemperatureModelsLoading(true);
    try {
      // Convert "ALL" to empty string for API call
      const statusFilterValue = temperatureStatusFilter === 'ALL' ? '' : temperatureStatusFilter;
      const params = statusFilterValue ? { status_filter: statusFilterValue } : {};
      const response = await axios.get(`${API}/admin/temperature-models`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setTemperatureModels(response.data || []);
    } catch (error) {
      console.error('Failed to load temperature models:', error);
    } finally {
      setTemperatureModelsLoading(false);
    }
  }, [token, temperatureStatusFilter]);

  // Fetch installed temperature profiles for comparison
  const fetchInstalledProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/installed-temperature-profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstalledProfiles(response.data || []);
    } catch (error) {
      console.error('Failed to load installed profiles:', error);
    }
  }, [token]);

  // Update temperature model status
  const handleUpdateTempModelStatus = async () => {
    if (!tempModelToReview || !tempReviewStatus) return;
    
    setUpdatingTempModel(true);
    try {
      await axios.put(
        `${API}/admin/temperature-models/${tempModelToReview.id}`,
        { 
          status: tempReviewStatus,
          review_notes: tempReviewNotes,
          is_public: tempIsPublic
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const publicMsg = tempIsPublic ? ' (Available to all users)' : '';
      toast.success(`Temperature model status updated to ${tempReviewStatus}${publicMsg}`);
      setShowTempModelReviewModal(false);
      setTempModelToReview(null);
      setTempReviewStatus('');
      setTempReviewNotes('');
      setTempIsPublic(false);
      
      await fetchTemperatureModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingTempModel(false);
    }
  };

  // Download temperature model file
  const handleDownloadTempFile = async (modelId, fileName) => {
    setDownloadingTempFileId(modelId);
    try {
      const response = await axios.get(
        `${API}/admin/temperature-models/${modelId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'temperature_model.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded');
    } catch (error) {
      // PRD task — surface the durable-storage error copy so admins immediately
      // understand the upload needs to be re-submitted.
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail.toLowerCase().includes('no longer available')) {
        toast.error(detail);
      } else {
        toast.error('Failed to download file');
      }
    } finally {
      setDownloadingTempFileId(null);
    }
  };

  // Fetch installed SGR and SFR models
  const fetchInstalledModels = useCallback(async () => {
    setInstalledModelsLoading(true);
    try {
      const [sgrRes, sfrRes] = await Promise.all([
        axios.get(`${API}/admin/installed-models/sgr`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/installed-models/sfr`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSgrModels(sgrRes.data || []);
      setSfrModels(sfrRes.data || []);
    } catch (error) {
      console.error('Failed to load installed models:', error);
    } finally {
      setInstalledModelsLoading(false);
    }
  }, [token]);

  // Fetch profile names for editing
  const fetchProfileNames = useCallback(async () => {
    setProfileNamesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/profile-names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileNames(response.data?.profiles || []);
    } catch (error) {
      console.error('Failed to load profile names:', error);
    } finally {
      setProfileNamesLoading(false);
    }
  }, [token]);

  // Fetch FCR Origins for versioning management
  const fetchFcrOrigins = useCallback(async () => {
    setFcrOriginsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/fcr-origins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFcrOrigins(response.data?.origins || []);
    } catch (error) {
      console.error('Failed to load FCR origins:', error);
    } finally {
      setFcrOriginsLoading(false);
    }
  }, [token]);

  // Security Dashboard fetch
  const fetchSecurityData = useCallback(async () => {
    setSecurityLoading(true);
    try {
      const response = await axios.get(`${API}/admin/security-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecurityData(response.data);
    } catch (error) {
      console.error('Failed to load security data:', error);
    } finally {
      setSecurityLoading(false);
    }
  }, [token]);

  // Support Inbox fetch
  const fetchSupportThreads = useCallback(async () => {
    setSupportLoading(true);
    try {
      const response = await axios.get(`${API}/messages/admin-inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupportThreads(response.data.threads);
    } catch (error) {
      console.error('Failed to load support threads:', error);
    } finally {
      setSupportLoading(false);
    }
  }, [token]);

  // ★ Onboarding video config — fetch & save
  const fetchOnboardingCfg = useCallback(async () => {
    setOnboardingCfgLoading(true);
    try {
      const res = await axios.get(`${API}/admin/onboarding-video-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnboardingCfg({
        enabled: res.data?.enabled !== false,
        video_url: res.data?.video_url || '',
        updated_by: res.data?.updated_by || null,
        updated_at: res.data?.updated_at || null,
        default_video_url: res.data?.default_video_url || '',
      });
      setOnboardingCfgDirty(false);
    } catch (error) {
      console.error('Failed to load onboarding video config:', error);
    } finally {
      setOnboardingCfgLoading(false);
    }
  }, [token]);

  const saveOnboardingCfg = async () => {
    setOnboardingCfgSaving(true);
    try {
      const res = await axios.patch(
        `${API}/admin/onboarding-video-config`,
        { enabled: onboardingCfg.enabled, video_url: onboardingCfg.video_url || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOnboardingCfg(prev => ({
        ...prev,
        enabled: res.data?.enabled !== false,
        video_url: res.data?.video_url || '',
        updated_by: res.data?.updated_by || null,
        updated_at: res.data?.updated_at || null,
      }));
      setOnboardingCfgDirty(false);
      toast.success('Onboarding video settings saved');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save onboarding video settings');
    } finally {
      setOnboardingCfgSaving(false);
    }
  };

  const resetOnboardingCfgToDefault = () => {
    setOnboardingCfg(prev => ({ ...prev, video_url: prev.default_video_url || '' }));
    setOnboardingCfgDirty(true);
  };

  const handleSupportReply = async (threadId) => {
    if (!supportReply.trim()) return;
    try {
      await axios.post(`${API}/messages/reply`, 
        { thread_id: threadId, message: supportReply.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSupportReply('');
      fetchSupportThreads();
      setActiveSupport(prev => {
        if (!prev) return null;
        return { ...prev, messages: [...prev.messages, { id: Date.now().toString(), sender: 'admin', sender_name: 'Admin', content: supportReply.trim(), timestamp: new Date().toISOString() }] };
      });
    } catch (error) {
      console.error('Reply failed:', error);
    }
  };

  const handleCloseThread = async (threadId) => {
    try {
      await axios.post(`${API}/messages/close`, { thread_id: threadId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSupportThreads();
      setActiveSupport(null);
    } catch (error) {
      console.error('Close failed:', error);
    }
  };

  // Update FCR version multiplier
  const handleUpdateFcrVersion = async (originCode, versionId) => {
    if (editingMultiplier < 50 || editingMultiplier > 150) {
      toast.error('Multiplier must be between 50% and 150%');
      return;
    }
    
    setSavingVersion(true);
    try {
      await axios.put(
        `${API}/admin/fcr-origins/${originCode}/versions/${versionId}`,
        { 
          multiplier_percent: editingMultiplier,
          version_name: editingVersionName || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('FCR version updated successfully');
      setEditingVersionId(null);
      setEditingMultiplier(100);
      setEditingVersionName('');
      fetchFcrOrigins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update version');
    } finally {
      setSavingVersion(false);
    }
  };

  // Set default FCR version
  const handleSetDefaultVersion = async (originCode, versionId) => {
    try {
      await axios.put(
        `${API}/admin/fcr-origins/${originCode}/default-version/${versionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Default version updated');
      fetchFcrOrigins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set default');
    }
  };

  // Add new FCR version
  const handleAddFcrVersion = async () => {
    if (newVersionMultiplier < 50 || newVersionMultiplier > 150) {
      toast.error('Multiplier must be between 50% and 150%');
      return;
    }
    
    setSavingVersion(true);
    try {
      await axios.post(
        `${API}/admin/fcr-origins/${addVersionOriginCode}/versions`,
        { 
          version_name: newVersionName || '',
          multiplier_percent: newVersionMultiplier,
          notes: newVersionNotes || ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('New version created successfully');
      setShowAddVersionModal(false);
      setAddVersionOriginCode('');
      setNewVersionName('');
      setNewVersionMultiplier(100);
      setNewVersionNotes('');
      fetchFcrOrigins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create version');
    } finally {
      setSavingVersion(false);
    }
  };

  // Delete FCR version
  const handleDeleteFcrVersion = async (originCode, versionId) => {
    if (!window.confirm('Are you sure you want to delete this version?')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API}/admin/fcr-origins/${originCode}/versions/${versionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Version deleted');
      fetchFcrOrigins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete version');
    }
  };

  // Update profile display name
  const handleUpdateProfileName = async (profileId) => {
    if (!editingProfileName.trim()) {
      toast.error('Display name cannot be empty');
      return;
    }
    
    setSavingProfileName(true);
    try {
      await axios.put(`${API}/admin/profile-names/${profileId}`, 
        { display_name: editingProfileName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Display name updated successfully');
      setEditingProfileId(null);
      setEditingProfileName('');
      // Refresh all related data
      fetchProfileNames();
      fetchInstalledModels();
      fetchInstalledProfiles();
    } catch (error) {
      toast.error('Failed to update display name');
    } finally {
      setSavingProfileName(false);
    }
  };

  // Reset profile name to original
  const handleResetProfileName = async (profileId, originalName) => {
    try {
      await axios.delete(`${API}/admin/profile-names/${profileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Display name reset to original');
      fetchProfileNames();
      fetchInstalledModels();
      fetchInstalledProfiles();
    } catch (error) {
      toast.error('Failed to reset display name');
    }
  };

  // Download installed model as Excel
  const handleDownloadInstalledModel = async (modelType, modelId, modelName) => {
    setDownloadingModelId(modelId);
    try {
      const endpoint = modelType === 'SGR' 
        ? `${API}/admin/installed-models/sgr/${modelId}/export`
        : `${API}/admin/installed-models/sfr/${modelId}/export`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${modelId}_model.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${modelName} exported to Excel`);
    } catch (error) {
      toast.error('Failed to export model');
    } finally {
      setDownloadingModelId(null);
    }
  };

  // Toggle compare selection
  const toggleCompareSelection = (modelRef) => {
    setSelectedCompareModels(prev => {
      if (prev.includes(modelRef)) {
        return prev.filter(m => m !== modelRef);
      } else if (prev.length < 5) {
        return [...prev, modelRef];
      }
      return prev;
    });
  };

  // Fetch comparison data
  const fetchComparisonData = async () => {
    if (selectedCompareModels.length < 2) {
      toast.error('Select at least 2 models to compare');
      return;
    }
    
    setComparisonLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/temperature-comparison`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { model_ids: selectedCompareModels.join(',') }
        }
      );
      setComparisonData(response.data);
    } catch (error) {
      toast.error('Failed to fetch comparison data');
    } finally {
      setComparisonLoading(false);
    }
  };

  // Fetch models for a specific client
  const fetchClientModels = useCallback(async (userId) => {
    setClientModelsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/clients/${userId}/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedClientModels(response.data || []);
    } catch (error) {
      console.error('Failed to load client models:', error);
      toast.error('Failed to load client models');
    } finally {
      setClientModelsLoading(false);
    }
  }, [token]);

  // Download model file (admin)
  const handleAdminDownloadFile = async (modelId, fileName) => {
    setDownloadingFileId(modelId);
    try {
      const response = await axios.get(
        `${API}/admin/custom-models/${modelId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'model.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download file');
    } finally {
      setDownloadingFileId(null);
    }
  };

  // Open assignment modal for a client
  const openAssignmentModal = async (client) => {
    setAssignmentClientId(client.user_id);
    setAssignmentClientEmail(client.user_email);
    
    // Fetch current assignment
    try {
      const assignmentRes = await axios.get(
        `${API}/admin/clients/${client.user_id}/assignment`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAssignmentForm({
        sgr_model_source: assignmentRes.data.sgr_model_source || 'DEFAULT',
        sgr_custom_model_id: assignmentRes.data.sgr_custom_model_id || '',
        fcr_model_source: assignmentRes.data.fcr_model_source || 'DEFAULT',
        fcr_custom_model_id: assignmentRes.data.fcr_custom_model_id || ''
      });
      
      // Fetch approved models for this client
      const modelsRes = await axios.get(
        `${API}/admin/clients/${client.user_id}/models`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const approved = (modelsRes.data || []).filter(m => 
        m.status === 'APPROVED_INSTALLED' || m.status === 'APPROVED'
      );
      setClientApprovedModels(approved);
      
      setShowAssignmentModal(true);
    } catch (error) {
      toast.error('Failed to load assignment data');
    }
  };

  // Save model assignment
  const handleSaveAssignment = async () => {
    if (!assignmentClientId) return;
    
    setSavingAssignment(true);
    try {
      await axios.put(
        `${API}/admin/clients/${assignmentClientId}/assignment`,
        {
          sgr_model_source: assignmentForm.sgr_model_source,
          sgr_custom_model_id: assignmentForm.sgr_model_source === 'CUSTOM' ? assignmentForm.sgr_custom_model_id : null,
          fcr_model_source: assignmentForm.fcr_model_source,
          fcr_custom_model_id: assignmentForm.fcr_model_source === 'CUSTOM' ? assignmentForm.fcr_custom_model_id : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Model assignment updated');
      setShowAssignmentModal(false);
      fetchClientsWithModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update assignment');
    } finally {
      setSavingAssignment(false);
    }
  };

  // Update custom model status
  const handleUpdateModelStatus = async () => {
    if (!modelToReview || !reviewStatus) return;
    
    setUpdatingModel(true);
    try {
      await axios.put(
        `${API}/admin/custom-models/${modelToReview.id}`,
        { status: reviewStatus, review_notes: reviewNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Model status updated to ${reviewStatus}`);
      setShowModelReviewModal(false);
      setModelToReview(null);
      setReviewStatus('');
      setReviewNotes('');
      
      // Refresh custom models and clients
      fetchCustomModels();
      fetchClientsWithModels();
      if (selectedClientId) fetchClientModels(selectedClientId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update model status');
    } finally {
      setUpdatingModel(false);
    }
  };

  // Fetch current override for selected species/genetic_origin
  const fetchCurrentOverride = useCallback(async () => {
    try {
      const hasGeneticOrigin = speciesWithGeneticOrigin.includes(selectedSpecies);
      const params = hasGeneticOrigin ? { genetic_origin: selectedGeneticOrigin } : {};
      
      const response = await axios.get(`${API}/admin/model-overrides/${selectedSpecies}`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setModelAdjustment(response.data.admin_model_adjustment_percent || 100);
      setFeedAdjustment(response.data.admin_daily_feed_adjustment_percent || 100);
    } catch (error) {
      // Reset to defaults if not found
      setModelAdjustment(100);
      setFeedAdjustment(100);
    }
  }, [token, selectedSpecies, selectedGeneticOrigin]);

  // Update overrides when species/genetic_origin changes
  useEffect(() => {
    if (token && user?.is_admin) {
      fetchCurrentOverride();
    }
  }, [token, user, selectedSpecies, selectedGeneticOrigin, fetchCurrentOverride]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchMetrics(), 
        fetchUsers(), 
        fetchModelOverrides(), 
        fetchCustomModels(), 
        fetchClientsWithModels(),
        fetchTemperatureModels(),
        fetchInstalledProfiles(),
        fetchInstalledModels(),
        fetchProfileNames(),
        fetchFcrOrigins(),
        fetchSecurityData(),
        fetchSupportThreads(),
        fetchOnboardingCfg()
      ]);
      setLoading(false);
    };
    if (token && user?.is_admin) {
      loadData();
    }
  }, [token, user, fetchMetrics, fetchUsers, fetchModelOverrides, fetchCustomModels, fetchClientsWithModels, fetchTemperatureModels, fetchInstalledProfiles, fetchInstalledModels, fetchProfileNames, fetchFcrOrigins, fetchSecurityData, fetchSupportThreads, fetchOnboardingCfg]);

  // Refresh custom models when filter changes
  useEffect(() => {
    if (token && user?.is_admin) {
      fetchCustomModels();
    }
  }, [customModelStatusFilter, fetchCustomModels, token, user]);

  // Refresh temperature models when filter changes
  useEffect(() => {
    if (token && user?.is_admin) {
      fetchTemperatureModels();
    }
  }, [temperatureStatusFilter, fetchTemperatureModels, token, user]);

  // Refresh data every 30 seconds
  useEffect(() => {
    if (!user?.is_admin) return;
    const interval = setInterval(() => {
      fetchMetrics();
      fetchUsers();
      fetchCustomModels();
      fetchClientsWithModels();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, fetchMetrics, fetchUsers, fetchCustomModels, fetchClientsWithModels]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchMetrics(), fetchUsers(), fetchModelOverrides(), fetchSecurityData(), fetchSupportThreads()]);
    setLoading(false);
    toast.success('Data refreshed');
  };

  // Save model override
  const saveModelOverride = async () => {
    setSavingOverride(true);
    try {
      const hasGeneticOrigin = speciesWithGeneticOrigin.includes(selectedSpecies);
      
      await axios.post(`${API}/admin/model-overrides`, {
        species: selectedSpecies,
        genetic_origin: hasGeneticOrigin ? selectedGeneticOrigin : null,
        admin_model_adjustment_percent: modelAdjustment,
        admin_daily_feed_adjustment_percent: feedAdjustment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Model override saved for ${selectedSpecies}${hasGeneticOrigin ? ` (${selectedGeneticOrigin})` : ''}`);
      setShowOverrideConfirmModal(false);
      fetchModelOverrides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save override');
    } finally {
      setSavingOverride(false);
    }
  };

  // Reset override to 100%
  const resetOverride = async (overrideId) => {
    try {
      await axios.delete(`${API}/admin/model-overrides/${overrideId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Override reset to 100%');
      fetchModelOverrides();
      fetchCurrentOverride();
    } catch (error) {
      toast.error('Failed to reset override');
    }
  };

  // Check if a user is protected (cannot be deleted)
  const isProtectedUser = (email) => {
    return PROTECTED_EMAILS.map(e => e.toLowerCase()).includes(email?.toLowerCase());
  };

  // Get deletable users (non-protected)
  const getDeletableUsers = () => {
    return users.filter(u => !isProtectedUser(u.email));
  };

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      // Select all non-protected users
      const deletableIds = getDeletableUsers().map(u => u.id);
      setSelectedUsers(new Set(deletableIds));
    } else {
      setSelectedUsers(new Set());
    }
  };

  // Handle individual row selection
  const handleSelectUser = (userId, checked) => {
    const newSelection = new Set(selectedUsers);
    if (checked) {
      newSelection.add(userId);
    } else {
      newSelection.delete(userId);
    }
    setSelectedUsers(newSelection);
    
    // Update selectAll state
    const deletableUsers = getDeletableUsers();
    setSelectAll(newSelection.size === deletableUsers.length && deletableUsers.length > 0);
  };

  // Delete selected users
  const handleDeleteSelected = async () => {
    if (selectedUsers.size === 0) return;
    
    setDeleting(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/delete`,
        { user_ids: Array.from(selectedUsers) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowDeleteModal(false);
      setSelectedUsers(new Set());
      setSelectAll(false);
      
      // Refresh data
      await Promise.all([fetchMetrics(), fetchUsers()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setDeleting(false);
    }
  };

  // Delete all users
  const handleDeleteAll = async () => {
    if (deleteAllConfirmText !== 'DELETE ALL') {
      toast.error('Please type "DELETE ALL" to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/delete-all`,
        { confirmation_text: deleteAllConfirmText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowDeleteAllModal(false);
      setDeleteAllConfirmText('');
      setDeleteAllStep(1);
      setSelectedUsers(new Set());
      setSelectAll(false);
      
      // Refresh data
      await Promise.all([fetchMetrics(), fetchUsers()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setDeleting(false);
    }
  };

  // Toggle block/unblock user
  const handleToggleBlock = async (userId, currentlyBlocked) => {
    try {
      const response = await axios.post(
        `${API}/admin/users/block`,
        { user_id: userId, block: !currentlyBlocked },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      // Refresh data
      await Promise.all([fetchMetrics(), fetchUsers()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleToggleApproval = async (userId, currentlyApproved) => {
    try {
      const response = await axios.post(
        `${API}/admin/users/approve`,
        { user_id: userId, approve: !currentlyApproved },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      // Refresh data
      await Promise.all([fetchMetrics(), fetchUsers()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user approval');
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // Admin password reset (per-user row action)
  // ─────────────────────────────────────────────────────────────────────
  // `passwordResetTarget` holds the user we're about to reset; null hides the
  // dialog. The new password is plaintext only inside this component state —
  // it's POSTed once and immediately cleared.
  const [passwordResetTarget, setPasswordResetTarget] = useState(null);
  const [passwordResetValue, setPasswordResetValue] = useState('');
  const [passwordResetSaving, setPasswordResetSaving] = useState(false);

  // Email history modal — per-user row action. Holds the user whose email
  // delivery log we want to inspect; null hides the modal. Every open of
  // this modal re-fetches from `/api/admin/users/{id}/email-history`.
  const [emailHistoryUser, setEmailHistoryUser] = useState(null);

  const openPasswordResetDialog = (user) => {
    setPasswordResetTarget(user);
    setPasswordResetValue('');
  };

  const closePasswordResetDialog = () => {
    setPasswordResetTarget(null);
    setPasswordResetValue('');
    setPasswordResetSaving(false);
  };

  const submitPasswordReset = async () => {
    if (!passwordResetTarget) return;
    if (!passwordResetValue || passwordResetValue.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPasswordResetSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/reset-password`,
        { user_id: passwordResetTarget.id, new_password: passwordResetValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || `Password reset for ${passwordResetTarget.email}`);
      closePasswordResetDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
      setPasswordResetSaving(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Email', 'Name', 'Registered', 'Last Login', 'Location', 'IP', 'Last Online', 'Status', 'Blocked', 'Total Analyses', 'Quota'];
    const rows = users.map(u => [
      u.email,
      u.name,
      u.created_at ? new Date(u.created_at).toLocaleDateString() : '',
      u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never',
      u.last_login_country ? `${u.last_login_city ? u.last_login_city + ', ' : ''}${u.last_login_country}` : '',
      u.last_login_ip || '',
      u.last_seen_at ? new Date(u.last_seen_at).toLocaleString() : 'Never',
      u.is_online ? 'Online' : 'Offline',
      u.is_blocked ? 'Yes' : 'No',
      u.total_analyses,
      u.quota_status
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pfd-users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  // SECURITY: Double-check admin access at render level
  const isAdminUser = user?.is_admin === true && user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  
  if (!isAdminUser) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] flex items-center justify-center">
        <div className="text-center">
          <Lock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600">Admin Access Denied</h2>
          <p className="text-gray-500 mt-2">Only authorized administrators can access this page.</p>
          <p className="text-xs text-gray-400 mt-1">Contact Total Production Solutions for access.</p>
          <Button onClick={() => navigate('/')} className="mt-4">Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  const deletableUsersCount = getDeletableUsers().length;
  const selectedCount = selectedUsers.size;

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200" data-testid="admin-console-page">
      <Header />
      
      {/* Admin Mode Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-2 px-4 text-center text-sm font-medium">
        <span className="inline-flex items-center gap-2">
          <Lock className="h-4 w-4" />
          Admin Mode - totalproductionsolutions
        </span>
      </div>

      {/* Pending Approvals Alert Banner */}
      {users && users.filter(u => u.quota_status === 'Pending').length > 0 && (
        <div className="bg-amber-500 text-white py-3 px-4" data-testid="pending-approvals-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full p-2">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="font-bold text-lg">
                  {users.filter(u => u.quota_status === 'Pending').length} User{users.filter(u => u.quota_status === 'Pending').length > 1 ? 's' : ''} Pending Approval
                </p>
                <p className="text-amber-100 text-sm">
                  New registration{users.filter(u => u.quota_status === 'Pending').length > 1 ? 's' : ''} waiting for your approval to access free analyses
                </p>
              </div>
            </div>
            <Button 
              variant="secondary" 
              size="sm"
              className="bg-white text-amber-600 hover:bg-amber-50 font-semibold"
              onClick={() => {
                setSearchQuery('');
                setFilterStatus('Pending');
              }}
            >
              Review Now
            </Button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-[#0891b2]">Admin Console</h1>
              <p className="text-gray-600">User & Activity Management</p>
            </div>
          </div>
          <Button onClick={handleRefresh} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* KPI Cards */}
        {metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-200" data-testid="kpi-total-users">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Users</p>
                    <p className="text-3xl font-bold text-[#0891b2]">{metrics.total_users}</p>
                    <p className="text-xs text-gray-400">+{metrics.new_users_7_days} last 7 days</p>
                  </div>
                  <Users className="h-10 w-10 text-[#0891b2]/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="kpi-active-users">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Active Users (7d)</p>
                    <p className="text-3xl font-bold text-green-600">{metrics.active_users_7_days}</p>
                    <p className="text-xs text-gray-400">{metrics.online_users} online now</p>
                  </div>
                  <Activity className="h-10 w-10 text-green-600/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="kpi-analyses">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Analyses (7d)</p>
                    <p className="text-3xl font-bold text-purple-600">{metrics.analyses_7_days}</p>
                    <p className="text-xs text-gray-400">{metrics.total_analyses_all_time} all-time</p>
                  </div>
                  <BarChart3 className="h-10 w-10 text-purple-600/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="kpi-quota-status">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">User Status</p>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="flex items-center gap-1 text-sm" title="Locked (no credits)">
                        <Lock className="h-4 w-4 text-red-500" />
                        <span className="font-semibold text-red-600">{metrics.locked_users}</span>
                      </span>
                      <span className="flex items-center gap-1 text-sm" title="Unlimited access">
                        <Unlock className="h-4 w-4 text-green-500" />
                        <span className="font-semibold text-green-600">{metrics.unlimited_users}</span>
                      </span>
                      <span className="flex items-center gap-1 text-sm" title="Blocked users">
                        <Ban className="h-4 w-4 text-orange-500" />
                        <span className="font-semibold text-orange-600">{metrics.blocked_users}</span>
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Locked / Unlimited / Blocked</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Feature Usage */}
        {metrics && (
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#0891b2]">Feature Usage (All-Time)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(metrics.feature_usage).map(([feature, count]) => (
                  <div key={feature} className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-[#0891b2]">{count}</p>
                    <p className="text-xs text-gray-500 capitalize">{feature.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admin-Only Feature Visibility — release eligible analytical
            features to normal authenticated users. Backend enforces the
            same gate; missing/null configuration fails closed. */}
        <AdminFeatureVisibilityPanel />

        {/* Phase 1 read-only detector for double-stocked cages that
            look like they were split into two independent populations. */}
        <SuspectedSplitsPanel />

        {/* Admin-only Missing-Fish baseline floor toggle. When enabled
            (default), admin viewers see a conservative 10 000-fish
            baseline instead of "None detected" and downstream RFI /
            FCR / revenue estimates treat the floor as a real detection. */}
        <AdminMissingFishBaselinePanel />

        {/* Support Inbox */}
        <Card className="border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50" data-testid="support-inbox-section">
          <CardHeader className="pb-3 cursor-pointer" onClick={() => setSupportExpanded(!supportExpanded)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-blue-700 flex items-center gap-2">
                <Inbox className="h-5 w-5" />
                Support Inbox
                {supportThreads.filter(t => t.status === 'open').length > 0 && (
                  <span className="text-xs font-medium bg-red-500 text-white px-2 py-0.5 rounded-full">
                    {supportThreads.filter(t => t.status === 'open').length} new
                  </span>
                )}
              </CardTitle>
              {supportExpanded ? <ChevronUp className="h-5 w-5 text-blue-400" /> : <ChevronDown className="h-5 w-5 text-blue-400" />}
            </div>
            <p className="text-sm text-gray-500 mt-1">User messages and support requests</p>
          </CardHeader>
          {supportExpanded && (
            <CardContent>
              {supportLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
              ) : activeSupport ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <button onClick={() => setActiveSupport(null)} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                      <ChevronDown className="h-4 w-4 rotate-90" /> Back to inbox
                    </button>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${activeSupport.status === 'open' ? 'bg-amber-100 text-amber-700' : activeSupport.status === 'answered' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {activeSupport.status}
                      </span>
                      {activeSupport.status !== 'closed' && (
                        <Button variant="outline" size="sm" onClick={() => handleCloseThread(activeSupport.thread_id)} className="text-xs h-7">Close</Button>
                      )}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border p-3">
                    <p className="text-sm font-medium text-slate-700">{activeSupport.subject}</p>
                    <p className="text-xs text-slate-400 mt-0.5">From: {activeSupport.user_name} ({activeSupport.user_email})</p>
                  </div>
                  <div className="bg-white rounded-lg border max-h-60 overflow-y-auto p-3 space-y-2">
                    {activeSupport.messages.map((msg, i) => (
                      <div key={msg.id || i} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                          msg.sender === 'admin' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-700 rounded-bl-sm'
                        }`}>
                          <p className={`text-[10px] mb-0.5 ${msg.sender === 'admin' ? 'text-blue-200' : 'text-slate-400'}`}>
                            {msg.sender_name} · {new Date(msg.timestamp).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {activeSupport.status !== 'closed' && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={supportReply}
                        onChange={e => setSupportReply(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSupportReply(activeSupport.thread_id)}
                        placeholder="Type your reply..."
                        className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                        data-testid="support-reply-input"
                      />
                      <Button onClick={() => handleSupportReply(activeSupport.thread_id)} disabled={!supportReply.trim()} size="sm" className="bg-blue-600 hover:bg-blue-700">
                        <Mail className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : supportThreads.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">No support messages yet</p>
              ) : (
                <div className="space-y-1.5">
                  {supportThreads.map(t => (
                    <button
                      key={t.thread_id}
                      onClick={() => setActiveSupport(t)}
                      className="w-full text-left bg-white p-3 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-slate-700 truncate pr-2">{t.subject}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${t.status === 'open' ? 'bg-amber-100 text-amber-700' : t.status === 'answered' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                          {t.status}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">From: {t.user_name}</p>
                      <p className="text-xs text-slate-400 truncate mt-0.5">{t.messages[t.messages.length - 1]?.content.slice(0, 80)}</p>
                      <p className="text-[10px] text-slate-300 mt-1">{new Date(t.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Security Dashboard */}
        <Card className="border-slate-200 bg-gradient-to-r from-red-50 to-rose-50" data-testid="security-dashboard-section">
          <CardHeader className="pb-3 cursor-pointer" onClick={() => setSecurityExpanded(!securityExpanded)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-rose-700 flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Dashboard
                <span className="text-xs font-normal text-rose-600 bg-rose-100 px-2 py-1 rounded-full">Live</span>
              </CardTitle>
              {securityExpanded ? <ChevronUp className="h-5 w-5 text-rose-400" /> : <ChevronDown className="h-5 w-5 text-rose-400" />}
            </div>
            <p className="text-sm text-gray-500 mt-1">Auth events, failed logins, and suspicious activity</p>
          </CardHeader>
          {securityExpanded && (
            <CardContent>
              {securityLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-rose-500" />
                </div>
              ) : securityData ? (
                <div className="space-y-5">
                  {/* Stats Row */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
                      <p className="text-2xl font-bold text-green-600" data-testid="sec-login-success-24h">{securityData.stats_24h.login_success}</p>
                      <p className="text-xs text-gray-500">Logins (24h)</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-red-200 text-center">
                      <p className="text-2xl font-bold text-red-600" data-testid="sec-login-failed-24h">{securityData.stats_24h.login_failed}</p>
                      <p className="text-xs text-gray-500">Failed (24h)</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-amber-200 text-center">
                      <p className="text-2xl font-bold text-amber-600" data-testid="sec-login-blocked-24h">{securityData.stats_24h.login_blocked}</p>
                      <p className="text-xs text-gray-500">Blocked (24h)</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200 text-center">
                      <p className="text-2xl font-bold text-blue-600" data-testid="sec-registrations-24h">{securityData.stats_24h.registrations}</p>
                      <p className="text-xs text-gray-500">Registrations (24h)</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                      <p className="text-2xl font-bold text-slate-600" data-testid="sec-total-7d">{securityData.stats_7d.total_events}</p>
                      <p className="text-xs text-gray-500">Total Events (7d)</p>
                    </div>
                  </div>

                  {/* 7-day Summary Bar */}
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">7-Day Summary</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-green-600 font-medium">{securityData.stats_7d.login_success} logins</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-red-600 font-medium">{securityData.stats_7d.login_failed} failed</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-amber-600 font-medium">{securityData.stats_7d.login_blocked} blocked</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-blue-600 font-medium">{securityData.stats_7d.registrations} registrations</span>
                    </div>
                  </div>

                  {/* Suspicious Activity */}
                  {(securityData.suspicious_ips.length > 0 || securityData.targeted_accounts.length > 0) && (
                    <div className="grid md:grid-cols-2 gap-4">
                      {securityData.suspicious_ips.length > 0 && (
                        <div className="bg-white rounded-lg p-3 border border-red-200">
                          <p className="text-xs font-medium text-red-600 mb-2 uppercase tracking-wide flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> Suspicious IPs (7d)
                          </p>
                          <div className="space-y-1">
                            {securityData.suspicious_ips.map((item, i) => (
                              <div key={item.ip || `sip-${i}`} className="flex justify-between text-sm">
                                <span className="text-gray-700 font-mono text-xs">
                                  {item.ip}
                                  {item.country && item.country !== 'Internal' && (
                                    <span className="ml-1.5 font-sans text-gray-500">({item.city ? item.city + ', ' : ''}{item.country})</span>
                                  )}
                                </span>
                                <span className="text-red-600 font-medium">{item.failed_attempts} failures</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {securityData.targeted_accounts.length > 0 && (
                        <div className="bg-white rounded-lg p-3 border border-amber-200">
                          <p className="text-xs font-medium text-amber-600 mb-2 uppercase tracking-wide flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> Targeted Accounts (7d)
                          </p>
                          <div className="space-y-1">
                            {securityData.targeted_accounts.map((item, i) => (
                              <div key={item.email || `tgt-${i}`} className="flex justify-between text-sm">
                                <span className="text-gray-700 truncate mr-2">{item.email}</span>
                                <span className="text-amber-600 font-medium">{item.failed_attempts} failures</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recent Events Log */}
                  <div className="bg-white rounded-lg border border-slate-200">
                    <p className="text-xs font-medium text-gray-500 px-3 py-2 border-b uppercase tracking-wide">Recent Auth Events</p>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm" data-testid="security-events-table">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Time</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Event</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">User</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">IP</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Location</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {securityData.recent_events.map((evt, i) => {
                            const eventColors = {
                              login_success: 'text-green-700 bg-green-50',
                              login_failed: 'text-red-700 bg-red-50',
                              login_blocked: 'text-amber-700 bg-amber-50',
                              register: 'text-blue-700 bg-blue-50'
                            };
                            const colorClass = eventColors[evt.event] || 'text-gray-700 bg-gray-50';
                            const ts = new Date(evt.timestamp);
                            const timeStr = ts.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
                            return (
                              <tr key={`${evt.timestamp}-${evt.event}-${evt.user_email}-${i}`} className={`border-b border-slate-100 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                                <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">{timeStr}</td>
                                <td className="px-3 py-1.5">
                                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${colorClass}`}>
                                    {evt.event.replace('_', ' ')}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-xs text-gray-700 truncate max-w-[180px]">{evt.user_email}</td>
                                <td className="px-3 py-1.5 text-xs text-gray-500 font-mono">{evt.ip}</td>
                                <td className="px-3 py-1.5 text-xs text-gray-500">
                                  {evt.country && evt.country !== 'Internal'
                                    ? `${evt.city ? evt.city + ', ' : ''}${evt.country}`
                                    : evt.country || '—'}
                                </td>
                                <td className="px-3 py-1.5 text-xs text-gray-400">{evt.details || '—'}</td>
                              </tr>
                            );
                          })}
                          {securityData.recent_events.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-400">No audit events recorded yet</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-4 text-center">No security data available</p>
              )}
            </CardContent>
          )}
        </Card>

        {/* Model Master Control - Admin Only */}
        <Card className="border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-[#0891b2] flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Model Master Control
              <span className="text-xs font-normal text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                Admin Only
              </span>
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Apply global SGR and FCR multipliers to all models. Changes affect all users platform-wide.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left: Override Configuration */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Species</label>
                  <select
                    value={selectedSpecies}
                    onChange={(e) => setSelectedSpecies(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md bg-white"
                    data-testid="model-species-select"
                  >
                    {allSpecies.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {speciesWithGeneticOrigin.includes(selectedSpecies) && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Genetic Origin</label>
                    <select
                      value={selectedGeneticOrigin}
                      onChange={(e) => setSelectedGeneticOrigin(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md bg-white"
                      data-testid="model-origin-select"
                    >
                      {geneticOrigins.map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Model Adjustment Factor (%)</label>
                    <Input
                      type="number"
                      min="50"
                      max="150"
                      value={modelAdjustment}
                      onChange={(e) => setModelAdjustment(parseFloat(e.target.value) || 100)}
                      className="bg-white"
                      data-testid="model-adjustment-input"
                    />
                    <p className="text-xs text-gray-500">Affects growth only (SGR)</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Daily Feed Adjustment (%)</label>
                    <Input
                      type="number"
                      min="50"
                      max="150"
                      value={feedAdjustment}
                      onChange={(e) => setFeedAdjustment(parseFloat(e.target.value) || 100)}
                      className="bg-white"
                      data-testid="feed-adjustment-input"
                    />
                    <p className="text-xs text-gray-500">Affects feed only (not growth)</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowOverrideConfirmModal(true)}
                    className="bg-[#0891b2] hover:bg-[#0e7490]"
                    data-testid="apply-override-btn"
                  >
                    Apply Update
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setModelAdjustment(100);
                      setFeedAdjustment(100);
                    }}
                    data-testid="reset-form-btn"
                  >
                    Reset Form to 100%
                  </Button>
                </div>
              </div>

              {/* Right: Active Overrides Table */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Active Overrides</label>
                <div className="border rounded-md bg-white overflow-hidden">
                  {modelOverrides.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      No active overrides. All models at 100%.
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-48">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium text-gray-600">Species</th>
                            <th className="text-left p-2 font-medium text-gray-600">Origin</th>
                            <th className="text-center p-2 font-medium text-gray-600">Model %</th>
                            <th className="text-center p-2 font-medium text-gray-600">Feed %</th>
                            <th className="text-center p-2 font-medium text-gray-600">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {modelOverrides.map((override) => (
                            <tr key={override.id} className="border-t hover:bg-gray-50">
                              <td className="p-2">{override.species}</td>
                              <td className="p-2">{override.genetic_origin || 'All'}</td>
                              <td className="p-2 text-center">
                                <span className={override.admin_model_adjustment_percent !== 100 ? 'text-amber-600 font-semibold' : ''}>
                                  {override.admin_model_adjustment_percent}%
                                </span>
                              </td>
                              <td className="p-2 text-center">
                                <span className={override.admin_daily_feed_adjustment_percent !== 100 ? 'text-amber-600 font-semibold' : ''}>
                                  {override.admin_daily_feed_adjustment_percent}%
                                </span>
                              </td>
                              <td className="p-2 text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => resetOverride(override.id)}
                                  className="text-red-500 hover:text-red-700 h-7 px-2"
                                >
                                  Reset
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Admin note: Model overrides apply platform-wide to all users.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Biological FCR Origin Versioning Section */}
        <Card className="border-slate-200 bg-gradient-to-r from-green-50 to-emerald-50" data-testid="fcr-origins-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => setFcrOriginsExpanded(!fcrOriginsExpanded)}>
                <CardTitle className="text-lg text-emerald-700 flex items-center gap-2">
                  <Percent className="h-5 w-5" />
                  Biological FCR Origin Versioning
                  <span className="text-xs font-normal text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                    {fcrOrigins.length} Origins
                  </span>
                </CardTitle>
                {fcrOriginsExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchFcrOrigins}
                disabled={fcrOriginsLoading}
                className="h-9"
              >
                <RefreshCw className={`h-4 w-4 ${fcrOriginsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Configure FCR multipliers for different genetic origins (Greek, French, Italian). Each origin supports multiple versions with custom % adjustments.
            </p>
          </CardHeader>
          
          {fcrOriginsExpanded && (
            <CardContent>
              {fcrOriginsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                </div>
              ) : fcrOrigins.length === 0 ? (
                <div className="text-center py-8 bg-white/50 rounded-lg border border-dashed border-emerald-300">
                  <Percent className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm">No FCR origins configured</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {fcrOrigins.map((origin) => (
                    <div 
                      key={origin.code} 
                      className="bg-white rounded-lg border border-emerald-200 p-4"
                      data-testid={`fcr-origin-${origin.code}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-semibold text-sm">
                            {origin.name}
                          </div>
                          <span className="text-xs text-gray-500">{origin.description}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setAddVersionOriginCode(origin.code);
                            setNewVersionName('');
                            setNewVersionMultiplier(100);
                            setNewVersionNotes('');
                            setShowAddVersionModal(true);
                          }}
                          className="h-8 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                          data-testid={`add-version-${origin.code}`}
                        >
                          + Add Version
                        </Button>
                      </div>
                      
                      {/* Versions Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-emerald-100 bg-emerald-50/50">
                              <th className="text-left py-2 px-3 font-medium text-gray-600">Version</th>
                              <th className="text-center py-2 px-3 font-medium text-gray-600">Multiplier</th>
                              <th className="text-center py-2 px-3 font-medium text-gray-600">Default</th>
                              <th className="text-left py-2 px-3 font-medium text-gray-600">Notes</th>
                              <th className="text-center py-2 px-3 font-medium text-gray-600">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(origin.versions || []).map((version) => (
                              <tr 
                                key={version.id} 
                                className={`border-b border-emerald-50 hover:bg-emerald-50/30 ${
                                  version.id === origin.default_version_id ? 'bg-emerald-50/50' : ''
                                }`}
                              >
                                <td className="py-2 px-3">
                                  {editingVersionId === version.id ? (
                                    <Input
                                      value={editingVersionName}
                                      onChange={(e) => setEditingVersionName(e.target.value)}
                                      className="h-7 text-sm w-24"
                                      placeholder={version.version_name}
                                    />
                                  ) : (
                                    <span className="font-medium text-gray-800" data-testid={`fcr-version-label-${version.id}`}>{version.label || version.version_name}</span>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  {editingVersionId === version.id ? (
                                    <Input
                                      type="number"
                                      min="50"
                                      max="150"
                                      value={editingMultiplier}
                                      onChange={(e) => setEditingMultiplier(parseFloat(e.target.value) || 100)}
                                      className="h-7 text-sm w-20 text-center mx-auto"
                                    />
                                  ) : (
                                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                                      version.multiplier_percent === 100 
                                        ? 'bg-gray-100 text-gray-600'
                                        : version.multiplier_percent > 100 
                                          ? 'bg-amber-100 text-amber-700'
                                          : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {version.multiplier_percent}%
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  {version.id === origin.default_version_id ? (
                                    <Star className="h-4 w-4 text-amber-500 mx-auto fill-amber-500" />
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleSetDefaultVersion(origin.code, version.id)}
                                      className="h-6 w-6 p-0 text-gray-400 hover:text-amber-500"
                                      title="Set as default"
                                    >
                                      <Star className="h-4 w-4" />
                                    </Button>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-gray-500 text-xs truncate max-w-[150px]" title={version.notes}>
                                  {version.notes || '-'}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  {editingVersionId === version.id ? (
                                    <div className="flex items-center justify-center gap-1">
                                      <Button
                                        size="sm"
                                        onClick={() => handleUpdateFcrVersion(origin.code, version.id)}
                                        disabled={savingVersion}
                                        className="h-7 bg-emerald-600 hover:bg-emerald-700"
                                      >
                                        {savingVersion ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setEditingVersionId(null);
                                          setEditingMultiplier(100);
                                          setEditingVersionName('');
                                        }}
                                        className="h-7"
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setEditingVersionId(version.id);
                                          setEditingMultiplier(version.multiplier_percent);
                                          setEditingVersionName(version.version_name);
                                        }}
                                        className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50"
                                        data-testid={`edit-version-${version.id}`}
                                      >
                                        <Edit className="h-3 w-3" />
                                      </Button>
                                      {(origin.versions || []).length > 1 && version.id !== origin.default_version_id && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleDeleteFcrVersion(origin.code, version.id)}
                                          className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                                          data-testid={`delete-version-${version.id}`}
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                  
                  {/* Info Box */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-4">
                    <p className="text-xs text-emerald-700">
                      <strong>Usage Logic:</strong> Base FCR model → Selected Origin → Selected Version Multiplier → Final FCR value.
                      <br />
                      Example: If Greek v2 has 105% multiplier, a base FCR of 1.50 becomes 1.575 (1.50 × 1.05).
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Upload Storage Health — durable object-storage availability check
            for every uploaded SGR/FCR/temperature model. Visible only here
            (admin-only page). */}
        <UploadStorageHealth token={token} />

        {/* Custom Model Approval Section - with Status Tickboxes */}
        <Card className="border-slate-200 bg-gradient-to-r from-cyan-50 to-blue-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => setCustomModelsExpanded(!customModelsExpanded)}>
                <CardTitle className="text-lg text-[#0891b2] flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Custom Model Approval
                  <span className="text-xs font-normal text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                    {customModels.filter(m => ['RECEIVED', 'PROCESSING', 'PRE_PROCESSED'].includes(m.status)).length} Pending
                  </span>
                </CardTitle>
                {customModelsExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={customModelStatusFilter}
                  onChange={(e) => setCustomModelStatusFilter(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white"
                  data-testid="custom-model-status-filter"
                >
                  <option value="">All Status</option>
                  <option value="RECEIVED">Received</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="APPROVED_INSTALLED">Approved & Installed</option>
                  <option value="REJECTED">Rejected</option>
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchCustomModels}
                  disabled={customModelsLoading}
                >
                  <RefreshCw className={`h-4 w-4 ${customModelsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Review and approve user-submitted SGR/FCR models with status tickboxes. Approved models become available only to the submitting user.
            </p>
          </CardHeader>
          
          {customModelsExpanded && (
            <CardContent>
              {customModelsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-[#0891b2]" />
                </div>
              ) : customModels.length === 0 ? (
                <div className="text-center py-8 bg-white/50 rounded-lg border border-dashed border-slate-300">
                  <FileText className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm">No custom models submitted yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="custom-models-table">
                    <thead>
                      <tr className="border-b border-slate-200 bg-white/50">
                        <th className="text-left py-3 px-3 font-medium text-gray-600">User</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-600">Model</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-600">Type</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-600">File</th>
                        <th className="text-center py-3 px-2 font-medium text-gray-600">
                          <div className="flex flex-col items-center">
                            <Upload className="h-3 w-3 mb-1" />
                            <span className="text-[10px]">Received</span>
                          </div>
                        </th>
                        <th className="text-center py-3 px-2 font-medium text-gray-600">
                          <div className="flex flex-col items-center">
                            <Clock className="h-3 w-3 mb-1" />
                            <span className="text-[10px]">Processing</span>
                          </div>
                        </th>
                        <th className="text-center py-3 px-2 font-medium text-gray-600">
                          <div className="flex flex-col items-center">
                            <CheckCircle2 className="h-3 w-3 mb-1" />
                            <span className="text-[10px]">Approved</span>
                          </div>
                        </th>
                        <th className="text-center py-3 px-3 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customModels.map((model) => {
                        const normalizedStatus = model.status === 'PRE_PROCESSED' ? 'PROCESSING' : 
                                                 model.status === 'APPROVED' ? 'APPROVED_INSTALLED' : model.status;
                        return (
                          <tr 
                            key={model.id} 
                            className={`border-b border-slate-100 hover:bg-white/80 transition-colors ${
                              normalizedStatus === 'RECEIVED' ? 'bg-blue-50/30' :
                              normalizedStatus === 'PROCESSING' ? 'bg-amber-50/30' :
                              normalizedStatus === 'APPROVED_INSTALLED' ? 'bg-green-50/30' :
                              normalizedStatus === 'REJECTED' ? 'bg-red-50/30' : ''
                            }`}
                          >
                            <td className="py-3 px-3">
                              <p className="text-xs text-gray-500 truncate max-w-[120px]" title={model.user_email}>
                                {model.user_email}
                              </p>
                            </td>
                            <td className="py-3 px-3">
                              <p className="font-medium text-gray-800">{model.model_name}</p>
                              <p className="text-xs text-gray-400">{model.species || 'Seabream'}</p>
                            </td>
                            <td className="py-3 px-3">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                model.model_type === 'SGR' ? 'bg-cyan-100 text-cyan-700' : 'bg-emerald-100 text-emerald-700'
                              }`}>
                                {model.model_type}
                              </span>
                            </td>
                            <td className="py-3 px-3">
                              {model.file_name ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAdminDownloadFile(model.id, model.file_name)}
                                  disabled={downloadingFileId === model.id}
                                  className="text-xs h-7 px-2"
                                >
                                  {downloadingFileId === model.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <>
                                      <Download className="h-3 w-3 mr-1" />
                                      {model.file_name.length > 15 ? model.file_name.slice(0, 15) + '...' : model.file_name}
                                    </>
                                  )}
                                </Button>
                              ) : (
                                <span className="text-xs text-gray-400">No file</span>
                              )}
                            </td>
                            {/* Status Tickboxes */}
                            <td className="py-3 px-2 text-center">
                              <input
                                type="checkbox"
                                checked={normalizedStatus === 'RECEIVED'}
                                onChange={() => {
                                  setModelToReview(model);
                                  setReviewStatus('RECEIVED');
                                  setReviewNotes('');
                                  setShowModelReviewModal(true);
                                }}
                                disabled={normalizedStatus === 'REJECTED'}
                                className="h-4 w-4 text-blue-600 rounded cursor-pointer disabled:opacity-50"
                              />
                            </td>
                            <td className="py-3 px-2 text-center">
                              <input
                                type="checkbox"
                                checked={normalizedStatus === 'PROCESSING'}
                                onChange={() => {
                                  setModelToReview(model);
                                  setReviewStatus('PROCESSING');
                                  setReviewNotes('');
                                  setShowModelReviewModal(true);
                                }}
                                disabled={normalizedStatus === 'REJECTED'}
                                className="h-4 w-4 text-amber-600 rounded cursor-pointer disabled:opacity-50"
                              />
                            </td>
                            <td className="py-3 px-2 text-center">
                              <input
                                type="checkbox"
                                checked={normalizedStatus === 'APPROVED_INSTALLED'}
                                onChange={() => {
                                  setModelToReview(model);
                                  setReviewStatus('APPROVED_INSTALLED');
                                  setReviewNotes('');
                                  setShowModelReviewModal(true);
                                }}
                                disabled={normalizedStatus === 'REJECTED'}
                                className="h-4 w-4 text-green-600 rounded cursor-pointer disabled:opacity-50"
                              />
                            </td>
                            <td className="py-3 px-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setModelToReview(model);
                                    setReviewStatus(normalizedStatus === 'RECEIVED' ? 'PROCESSING' : 
                                                   normalizedStatus === 'PROCESSING' ? 'APPROVED_INSTALLED' : '');
                                    setReviewNotes(model.review_notes || '');
                                    setShowModelReviewModal(true);
                                  }}
                                  className="text-[#0891b2] hover:bg-[#0891b2]/10 h-7"
                                  data-testid={`review-model-${model.id}`}
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Review
                                </Button>
                                {normalizedStatus !== 'REJECTED' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setModelToReview(model);
                                      setReviewStatus('REJECTED');
                                      setReviewNotes('');
                                      setShowModelReviewModal(true);
                                    }}
                                    className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                                    title="Reject"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Temperature Model Uploads Section */}
        <Card className="border-slate-200 bg-gradient-to-r from-orange-50 to-amber-50" data-testid="temp-model-uploads-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => setTemperatureModelsExpanded(!temperatureModelsExpanded)}>
                <CardTitle className="text-lg text-orange-700 flex items-center gap-2">
                  <Thermometer className="h-5 w-5" />
                  Client Temperature Model Uploads
                  <span className="text-xs font-normal text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                    {temperatureModels.length} Models
                  </span>
                </CardTitle>
                {temperatureModelsExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div className="flex items-center gap-2">
                <Select value={temperatureStatusFilter} onValueChange={setTemperatureStatusFilter}>
                  <SelectTrigger className="w-40 h-9 text-xs bg-white" data-testid="temp-status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Statuses</SelectItem>
                    <SelectItem value="RECEIVED">Received</SelectItem>
                    <SelectItem value="PROCESSING">Processing</SelectItem>
                    <SelectItem value="APPROVED_INSTALLED">Approved</SelectItem>
                    <SelectItem value="REJECTED">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchTemperatureModels}
                  disabled={temperatureModelsLoading}
                  className="h-9"
                >
                  <RefreshCw className={`h-4 w-4 ${temperatureModelsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
          
          {temperatureModelsExpanded && (
            <CardContent>
              {temperatureModelsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                </div>
              ) : temperatureModels.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Thermometer className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm">No temperature models found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="temp-models-admin-table">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">User</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Model Name</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Year Type</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Submitted</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {temperatureModels.map((model) => (
                        <tr 
                          key={model.id} 
                          className={`border-b border-slate-100 hover:bg-white/80 transition-colors ${
                            model.status === 'APPROVED_INSTALLED' ? 'bg-green-50/30' :
                            model.status === 'REJECTED' ? 'bg-red-50/30' : ''
                          }`}
                        >
                          <td className="py-2 px-3">
                            <p className="text-gray-800">{model.user_email}</p>
                            <p className="text-xs text-gray-400">{model.user_id?.substring(0, 8)}...</p>
                          </td>
                          <td className="py-2 px-3">
                            <p className="font-medium text-gray-800">{model.name}</p>
                            {model.description && (
                              <p className="text-xs text-gray-500 truncate max-w-[150px]">{model.description}</p>
                            )}
                          </td>
                          <td className="py-2 px-3 text-xs text-gray-600">
                            {model.year_type || '365-day'}
                          </td>
                          <td className="py-2 px-3 text-xs text-gray-500">
                            {model.submitted_at ? new Date(model.submitted_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex flex-col gap-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                                model.status === 'RECEIVED' ? 'bg-blue-100 text-blue-700' :
                                model.status === 'PROCESSING' ? 'bg-amber-100 text-amber-700' :
                                model.status === 'APPROVED_INSTALLED' ? 'bg-green-100 text-green-700' :
                                model.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {model.status === 'RECEIVED' && <Upload className="h-3 w-3" />}
                                {model.status === 'PROCESSING' && <Clock className="h-3 w-3" />}
                                {model.status === 'APPROVED_INSTALLED' && <CheckCircle2 className="h-3 w-3" />}
                                {model.status === 'REJECTED' && <XCircle className="h-3 w-3" />}
                                {model.status === 'APPROVED_INSTALLED' ? 'Approved' : model.status}
                              </span>
                              {model.is_public && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                                  <Users className="h-3 w-3" />
                                  Public
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex items-center justify-center gap-1">
                              {model.file_name && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadTempFile(model.id, model.file_name)}
                                  disabled={downloadingTempFileId === model.id}
                                  className="h-8 w-8 p-0 text-gray-500 hover:text-orange-600"
                                  title="Download file"
                                >
                                  {downloadingTempFileId === model.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setTempModelToReview(model);
                                  setTempReviewStatus(model.status);
                                  setTempReviewNotes(model.review_notes || '');
                                  setTempIsPublic(model.is_public || false);
                                  setShowTempModelReviewModal(true);
                                }}
                                className="h-8 w-8 p-0 text-orange-600 hover:text-orange-800"
                                title="Review & Update Status"
                              >
                                <Settings2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Temperature Model Comparison Section */}
        <Card className="border-slate-200 bg-gradient-to-r from-cyan-50 to-teal-50" data-testid="temp-comparison-section">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setTempComparisonExpanded(!tempComparisonExpanded)}>
              <CardTitle className="text-lg text-cyan-700 flex items-center gap-2">
                <LineChart className="h-5 w-5" />
                Temperature Model Comparison
              </CardTitle>
              {tempComparisonExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </CardHeader>
          
          {tempComparisonExpanded && (
            <CardContent className="space-y-4">
              {/* Selection Area */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Installed Profiles */}
                <div className="p-3 bg-white rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Installed Profiles</p>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {installedProfiles.map(profile => (
                      <label 
                        key={profile.id} 
                        className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <Checkbox 
                          checked={selectedCompareModels.includes(profile.id)}
                          onCheckedChange={() => toggleCompareSelection(profile.id)}
                        />
                        <span className="text-sm text-gray-700">{profile.name}</span>
                        <span className="text-xs text-gray-400 ml-auto">
                          {profile.avg_temp}°C avg
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Custom Models */}
                <div className="p-3 bg-white rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Custom Temperature Models</p>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {temperatureModels.filter(m => m.status === 'APPROVED_INSTALLED').length === 0 ? (
                      <p className="text-xs text-gray-400 py-2">No approved custom models</p>
                    ) : (
                      temperatureModels.filter(m => m.status === 'APPROVED_INSTALLED').map(model => (
                        <label 
                          key={model.id} 
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <Checkbox 
                            checked={selectedCompareModels.includes(model.id)}
                            onCheckedChange={() => toggleCompareSelection(model.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-700">{model.name}</span>
                            <p className="text-xs text-gray-400 truncate">{model.user_email}</p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
              
              {/* Compare Button */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={fetchComparisonData}
                  disabled={selectedCompareModels.length < 2 || comparisonLoading}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="compare-temp-btn"
                >
                  {comparisonLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <LineChart className="h-4 w-4 mr-2" />
                  )}
                  Compare ({selectedCompareModels.length} selected)
                </Button>
                {selectedCompareModels.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setSelectedCompareModels([])}
                    size="sm"
                  >
                    Clear Selection
                  </Button>
                )}
              </div>
              
              {/* Comparison Chart */}
              {comparisonData && comparisonData.models?.length > 0 && (
                <div className="space-y-4">
                  {/* Chart */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Temperature Comparison (Jan - Dec)</h4>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsLineChart
                          data={(() => {
                            // Build chart data from comparison models
                            const days = [];
                            for (let i = 0; i < 365; i++) {
                              const dayData = { day: i + 1 };
                              comparisonData.models.forEach(model => {
                                if (model.temperatures && model.temperatures[i] !== undefined) {
                                  dayData[model.name] = model.temperatures[i];
                                }
                              });
                              days.push(dayData);
                            }
                            return days;
                          })()}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            dataKey="day" 
                            tick={{ fontSize: 10 }}
                            tickFormatter={(day) => {
                              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                              const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
                              let cumulative = 0;
                              for (let i = 0; i < 12; i++) {
                                if (day <= cumulative + daysPerMonth[i]) {
                                  return day === cumulative + 1 ? months[i] : '';
                                }
                                cumulative += daysPerMonth[i];
                              }
                              return '';
                            }}
                          />
                          <YAxis 
                            tick={{ fontSize: 10 }} 
                            label={{ value: '°C', angle: -90, position: 'insideLeft', fontSize: 10 }}
                          />
                          <Tooltip 
                            formatter={(value) => [`${value.toFixed(1)}°C`]}
                            labelFormatter={(day) => `Day ${day}`}
                          />
                          <Legend />
                          {comparisonData.models.map((model, index) => {
                            const colors = ['#0891b2', '#ea580c', '#16a34a', '#7c3aed', '#dc2626'];
                            return (
                              <Line 
                                key={model.id}
                                type="monotone" 
                                dataKey={model.name}
                                stroke={colors[index % colors.length]}
                                dot={false}
                                strokeWidth={1.5}
                              />
                            );
                          })}
                        </RechartsLineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  
                  {/* Statistics */}
                  <div className="grid md:grid-cols-3 gap-3">
                    {comparisonData.models.map((model, index) => {
                      const colors = ['border-cyan-300 bg-cyan-50', 'border-orange-300 bg-orange-50', 'border-green-300 bg-green-50', 'border-purple-300 bg-purple-50', 'border-red-300 bg-red-50'];
                      return (
                        <div key={model.id} className={`p-3 rounded-lg border ${colors[index % colors.length]}`}>
                          <p className="font-medium text-gray-800 text-sm truncate" title={model.name}>{model.name}</p>
                          <p className="text-xs text-gray-500 mb-2">{model.type === 'custom' ? model.user_email : 'Installed Profile'}</p>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <p className="text-gray-500">Min</p>
                              <p className="font-medium">{model.min_temp}°C</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Avg</p>
                              <p className="font-medium">{model.avg_temp}°C</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Max</p>
                              <p className="font-medium">{model.max_temp}°C</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Differences (if 2 models) */}
                  {comparisonData.differences && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800 text-sm">Comparison Summary</p>
                          <div className="grid grid-cols-3 gap-4 mt-2 text-xs">
                            <div>
                              <p className="text-amber-600">Max Difference</p>
                              <p className="font-bold text-amber-800">{comparisonData.differences.max_difference}°C</p>
                            </div>
                            <div>
                              <p className="text-amber-600">Avg Difference</p>
                              <p className="font-bold text-amber-800">{comparisonData.differences.avg_difference}°C</p>
                            </div>
                            <div>
                              <p className="text-amber-600">Days &gt; 2°C diff</p>
                              <p className="font-bold text-amber-800">{comparisonData.differences.days_exceeding_2c} days</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Installed SGR & SFR Models Section */}
        <Card className="border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50" data-testid="installed-models-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => setInstalledModelsExpanded(!installedModelsExpanded)}>
                <CardTitle className="text-lg text-emerald-700 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Installed Growth Models (SGR & SFR)
                </CardTitle>
                {installedModelsExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchInstalledModels}
                disabled={installedModelsLoading}
                className="h-9"
              >
                <RefreshCw className={`h-4 w-4 ${installedModelsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          
          {installedModelsExpanded && (
            <CardContent className="space-y-6">
              {installedModelsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                </div>
              ) : (
                <>
                  {/* SGR Models */}
                  <div>
                    <h4 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      SGR Models (Specific Growth Rate)
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {sgrModels.map((model) => (
                        <div 
                          key={model.id}
                          className="p-4 bg-white rounded-lg border border-emerald-200 hover:border-emerald-400 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h5 className="font-medium text-gray-800">{model.name}</h5>
                              <p className="text-xs text-gray-500 mt-1">{model.description}</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadInstalledModel('SGR', model.id, model.name)}
                              disabled={downloadingModelId === model.id}
                              className="h-8 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                              data-testid={`export-${model.id}`}
                            >
                              {downloadingModelId === model.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                            <div className="p-2 bg-emerald-50 rounded">
                              <p className="text-gray-500">Species</p>
                              <p className="font-medium text-emerald-700">{model.species}</p>
                            </div>
                            {model.total_days != null ? (
                              <>
                                <div className="p-2 bg-emerald-50 rounded">
                                  <p className="text-gray-500">Total Days</p>
                                  <p className="font-medium text-emerald-700">{model.total_days}</p>
                                </div>
                                <div className="p-2 bg-emerald-50 rounded">
                                  <p className="text-gray-500">Weight Range</p>
                                  <p className="font-medium text-emerald-700">{model.min_weight}g - {model.max_weight}g</p>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="p-2 bg-emerald-50 rounded">
                                  <p className="text-gray-500">Weight Range</p>
                                  <p className="font-medium text-emerald-700">{model.weight_range}</p>
                                </div>
                                <div className="p-2 bg-emerald-50 rounded">
                                  <p className="text-gray-500">Temp Range</p>
                                  <p className="font-medium text-emerald-700">{model.temp_range}</p>
                                </div>
                              </>
                            )}
                          </div>
                          {model.total_days == null && model.total_entries != null && (
                            <div className="mt-2 text-xs text-gray-400">
                              {model.total_entries} data points ({model.unique_weights} weights × {model.unique_temperatures} temps)
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* SFR/BFCR Models */}
                  <div>
                    <h4 className="text-sm font-semibold text-teal-700 mb-3 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      SFR & BFCR Models (Feed Rate & Conversion)
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {sfrModels.map((model) => (
                        <div 
                          key={model.id}
                          className="p-4 bg-white rounded-lg border border-teal-200 hover:border-teal-400 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h5 className="font-medium text-gray-800">{model.name}</h5>
                              <p className="text-xs text-gray-500 mt-1">{model.description}</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadInstalledModel('SFR', model.id, model.name)}
                              disabled={downloadingModelId === model.id}
                              className="h-8 text-teal-600 border-teal-300 hover:bg-teal-50"
                              data-testid={`export-${model.id}`}
                            >
                              {downloadingModelId === model.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                            <div className="p-2 bg-teal-50 rounded">
                              <p className="text-gray-500">Species</p>
                              <p className="font-medium text-teal-700">{model.species}</p>
                            </div>
                            <div className="p-2 bg-teal-50 rounded">
                              <p className="text-gray-500">Weight Range</p>
                              <p className="font-medium text-teal-700">{model.weight_range}</p>
                            </div>
                            {model.temp_range ? (
                              <div className="p-2 bg-teal-50 rounded">
                                <p className="text-gray-500">Temp Range</p>
                                <p className="font-medium text-teal-700">{model.temp_range}</p>
                              </div>
                            ) : (
                              <div className="p-2 bg-teal-50 rounded">
                                <p className="text-gray-500">Lookup</p>
                                <p className="font-medium text-teal-700">Weight-indexed</p>
                              </div>
                            )}
                          </div>
                          <div className="mt-2 text-xs text-gray-400">
                            {model.total_entries} data points
                            {model.unique_temperatures
                              ? ` (${model.unique_weights} weights × ${model.unique_temperatures} temps)`
                              : ` (${model.unique_weights} weight rows)`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          )}
        </Card>

        {/* Edit Profile Names Section */}
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg text-amber-700 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit Profile Display Names
                  <span className="text-xs font-normal text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                    {profileNames.length} Profiles
                  </span>
                </CardTitle>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchProfileNames}
                disabled={profileNamesLoading}
                className="h-9"
              >
                <RefreshCw className={`h-4 w-4 ${profileNamesLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Customize display names for installed temperature profiles and SGR models.
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {profileNamesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
              </div>
            ) : (
              <>
                {/* Temperature Profiles */}
                <div>
                  <h4 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                    <Thermometer className="h-4 w-4" />
                    Temperature Profiles
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="temp-profile-names-table">
                      <thead>
                        <tr className="border-b border-amber-200 bg-white/50">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Original Name</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Display Name</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profileNames.filter(p => p.profile_type === 'temperature').map((profile) => (
                          <tr key={profile.profile_id} className="border-b border-amber-100 hover:bg-white/50">
                            <td className="py-2 px-3 text-gray-600">{profile.original_name}</td>
                            <td className="py-2 px-3">
                              {editingProfileId === profile.profile_id ? (
                                <Input
                                  value={editingProfileName}
                                  onChange={(e) => setEditingProfileName(e.target.value)}
                                  className="h-8 text-sm"
                                  placeholder="Enter display name"
                                  data-testid={`edit-input-${profile.profile_id}`}
                                />
                              ) : (
                                <span className={profile.display_name !== profile.original_name ? 'text-amber-700 font-medium' : 'text-gray-700'}>
                                  {profile.display_name}
                                  {profile.display_name !== profile.original_name && (
                                    <span className="ml-2 text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded">edited</span>
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {editingProfileId === profile.profile_id ? (
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateProfileName(profile.profile_id)}
                                    disabled={savingProfileName}
                                    className="h-7 bg-amber-600 hover:bg-amber-700"
                                    data-testid={`save-${profile.profile_id}`}
                                  >
                                    {savingProfileName ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setEditingProfileId(null); setEditingProfileName(''); }}
                                    className="h-7"
                                    data-testid={`cancel-${profile.profile_id}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setEditingProfileId(profile.profile_id); setEditingProfileName(profile.display_name); }}
                                    className="h-7 text-amber-600 border-amber-300 hover:bg-amber-50"
                                    data-testid={`edit-${profile.profile_id}`}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  {profile.display_name !== profile.original_name && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleResetProfileName(profile.profile_id, profile.original_name)}
                                      className="h-7 text-gray-600 border-gray-300 hover:bg-gray-50"
                                      data-testid={`reset-${profile.profile_id}`}
                                    >
                                      <RotateCcw className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* SGR Models */}
                <div>
                  <h4 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    SGR Models
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="sgr-profile-names-table">
                      <thead>
                        <tr className="border-b border-amber-200 bg-white/50">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Species</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Original Name</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Display Name</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profileNames.filter(p => p.profile_type === 'sgr').map((profile) => (
                          <tr key={profile.profile_id} className="border-b border-amber-100 hover:bg-white/50">
                            <td className="py-2 px-3 text-gray-600">{profile.species}</td>
                            <td className="py-2 px-3 text-gray-600">{profile.original_name}</td>
                            <td className="py-2 px-3">
                              {editingProfileId === profile.profile_id ? (
                                <Input
                                  value={editingProfileName}
                                  onChange={(e) => setEditingProfileName(e.target.value)}
                                  className="h-8 text-sm"
                                  placeholder="Enter display name"
                                  data-testid={`edit-input-${profile.profile_id}`}
                                />
                              ) : (
                                <span className={profile.display_name !== profile.original_name ? 'text-amber-700 font-medium' : 'text-gray-700'}>
                                  {profile.display_name}
                                  {profile.display_name !== profile.original_name && (
                                    <span className="ml-2 text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded">edited</span>
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {editingProfileId === profile.profile_id ? (
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateProfileName(profile.profile_id)}
                                    disabled={savingProfileName}
                                    className="h-7 bg-amber-600 hover:bg-amber-700"
                                    data-testid={`save-${profile.profile_id}`}
                                  >
                                    {savingProfileName ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setEditingProfileId(null); setEditingProfileName(''); }}
                                    className="h-7"
                                    data-testid={`cancel-${profile.profile_id}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setEditingProfileId(profile.profile_id); setEditingProfileName(profile.display_name); }}
                                    className="h-7 text-amber-600 border-amber-300 hover:bg-amber-50"
                                    data-testid={`edit-${profile.profile_id}`}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  {profile.display_name !== profile.original_name && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleResetProfileName(profile.profile_id, profile.original_name)}
                                      className="h-7 text-gray-600 border-gray-300 hover:bg-gray-50"
                                      data-testid={`reset-${profile.profile_id}`}
                                    >
                                      <RotateCcw className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Client Model Uploads Section */}
        <Card className="border-slate-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => setClientModelsSectionExpanded(!clientModelsSectionExpanded)}>
                <CardTitle className="text-lg text-purple-700 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Client Model Uploads
                  <span className="text-xs font-normal text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                    {clientsWithModels.length} Clients
                  </span>
                </CardTitle>
                {clientModelsSectionExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchClientsWithModels}
                disabled={clientsLoading}
              >
                <RefreshCw className={`h-4 w-4 ${clientsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Browse clients by username, download files, and assign default vs custom models per client.
            </p>
          </CardHeader>

          {clientModelsSectionExpanded && (
            <CardContent>
              {clientsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                </div>
              ) : clientsWithModels.length === 0 ? (
                <div className="text-center py-8 bg-white/50 rounded-lg border border-dashed border-purple-200">
                  <Users className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm">No clients have uploaded models yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Clients Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="clients-models-table">
                      <thead>
                        <tr className="border-b border-purple-200 bg-white/50">
                          <th className="text-left py-3 px-3 font-medium text-gray-600">Client Email</th>
                          <th className="text-center py-3 px-3 font-medium text-gray-600">SGR</th>
                          <th className="text-center py-3 px-3 font-medium text-gray-600">FCR</th>
                          <th className="text-center py-3 px-3 font-medium text-gray-600">Approved</th>
                          <th className="text-center py-3 px-3 font-medium text-gray-600">Pending</th>
                          <th className="text-left py-3 px-3 font-medium text-gray-600">Last Upload</th>
                          <th className="text-center py-3 px-3 font-medium text-gray-600">Assignment</th>
                          <th className="text-center py-3 px-3 font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientsWithModels.map((client) => (
                          <tr 
                            key={client.user_id} 
                            className={`border-b border-purple-100 hover:bg-white/80 transition-colors ${
                              selectedClientId === client.user_id ? 'bg-purple-100/50' : ''
                            }`}
                          >
                            <td className="py-3 px-3">
                              <p className="font-medium text-gray-800 truncate max-w-[200px]" title={client.user_email}>
                                {client.user_email}
                              </p>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className="px-2 py-0.5 text-xs font-medium rounded bg-cyan-100 text-cyan-700">
                                {client.sgr_count}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-100 text-emerald-700">
                                {client.fcr_count}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">
                                {client.approved_count}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center">
                              {client.pending_count > 0 ? (
                                <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700">
                                  {client.pending_count}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-gray-500 text-xs">
                              {client.last_upload ? new Date(client.last_upload).toLocaleDateString() : '-'}
                            </td>
                            <td className="py-3 px-3 text-center">
                              <div className="flex flex-col items-center gap-1 text-xs">
                                <span className={`px-1.5 py-0.5 rounded ${
                                  client.sgr_model_source === 'CUSTOM' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  SGR: {client.sgr_model_source === 'CUSTOM' ? 'Custom' : 'Default'}
                                </span>
                                <span className={`px-1.5 py-0.5 rounded ${
                                  client.fcr_model_source === 'CUSTOM' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  FCR: {client.fcr_model_source === 'CUSTOM' ? 'Custom' : 'Default'}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedClientId(client.user_id);
                                    fetchClientModels(client.user_id);
                                  }}
                                  className="h-7 text-xs"
                                  data-testid={`view-client-uploads-${client.user_id}`}
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Uploads
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openAssignmentModal(client)}
                                  className="h-7 text-xs text-purple-600 hover:bg-purple-50"
                                  data-testid={`assign-models-${client.user_id}`}
                                >
                                  <Settings2 className="h-3 w-3 mr-1" />
                                  Assign
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Selected Client's Models */}
                  {selectedClientId && (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-800 flex items-center gap-2">
                          <FileSpreadsheet className="h-4 w-4 text-purple-600" />
                          Models for: {clientsWithModels.find(c => c.user_id === selectedClientId)?.user_email}
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedClientId(null);
                            setSelectedClientModels([]);
                          }}
                          className="h-7 text-xs"
                        >
                          Close
                        </Button>
                      </div>

                      {clientModelsLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                        </div>
                      ) : selectedClientModels.length === 0 ? (
                        <p className="text-center text-gray-500 py-4 text-sm">No models found</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-gray-50">
                                <th className="text-left py-2 px-3 font-medium text-gray-600">Model Name</th>
                                <th className="text-left py-2 px-3 font-medium text-gray-600">Type</th>
                                <th className="text-left py-2 px-3 font-medium text-gray-600">Upload Date</th>
                                <th className="text-left py-2 px-3 font-medium text-gray-600">File</th>
                                <th className="text-center py-2 px-2 font-medium text-gray-600">Received</th>
                                <th className="text-center py-2 px-2 font-medium text-gray-600">Processing</th>
                                <th className="text-center py-2 px-2 font-medium text-gray-600">Approved</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedClientModels.map((model) => {
                                const normalizedStatus = model.status === 'PRE_PROCESSED' ? 'PROCESSING' : 
                                                        model.status === 'APPROVED' ? 'APPROVED_INSTALLED' : model.status;
                                return (
                                  <tr key={model.id} className="border-b border-gray-100">
                                    <td className="py-2 px-3">
                                      <p className="font-medium text-gray-800">{model.model_name}</p>
                                      <p className="text-xs text-gray-400">{model.species}</p>
                                    </td>
                                    <td className="py-2 px-3">
                                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                        model.model_type === 'SGR' ? 'bg-cyan-100 text-cyan-700' : 'bg-emerald-100 text-emerald-700'
                                      }`}>
                                        {model.model_type}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3 text-gray-500 text-xs">
                                      {model.submitted_at ? new Date(model.submitted_at).toLocaleDateString() : '-'}
                                    </td>
                                    <td className="py-2 px-3">
                                      {model.file_name ? (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleAdminDownloadFile(model.id, model.file_name)}
                                          disabled={downloadingFileId === model.id}
                                          className="text-xs h-6 px-2"
                                        >
                                          {downloadingFileId === model.id ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                          ) : (
                                            <Download className="h-3 w-3" />
                                          )}
                                        </Button>
                                      ) : '-'}
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      <input
                                        type="checkbox"
                                        checked={normalizedStatus === 'RECEIVED'}
                                        onChange={() => {
                                          setModelToReview(model);
                                          setReviewStatus('RECEIVED');
                                          setShowModelReviewModal(true);
                                        }}
                                        disabled={normalizedStatus === 'REJECTED'}
                                        className="h-4 w-4 text-blue-600 rounded cursor-pointer disabled:opacity-50"
                                      />
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      <input
                                        type="checkbox"
                                        checked={normalizedStatus === 'PROCESSING'}
                                        onChange={() => {
                                          setModelToReview(model);
                                          setReviewStatus('PROCESSING');
                                          setShowModelReviewModal(true);
                                        }}
                                        disabled={normalizedStatus === 'REJECTED'}
                                        className="h-4 w-4 text-amber-600 rounded cursor-pointer disabled:opacity-50"
                                      />
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      <input
                                        type="checkbox"
                                        checked={normalizedStatus === 'APPROVED_INSTALLED'}
                                        onChange={() => {
                                          setModelToReview(model);
                                          setReviewStatus('APPROVED_INSTALLED');
                                          setShowModelReviewModal(true);
                                        }}
                                        disabled={normalizedStatus === 'REJECTED'}
                                        className="h-4 w-4 text-green-600 rounded cursor-pointer disabled:opacity-50"
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          )}
        </Card>


        {/* ★ Onboarding Video Settings — global config for the first-login video modal */}
        <Card className="border-slate-200" data-testid="onboarding-video-settings-card">
          <CardHeader
            className="pb-3 cursor-pointer select-none"
            onClick={() => setOnboardingCfgExpanded(v => !v)}
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-lg text-[#0891b2] flex items-center gap-2">
                <PlayCircle className="h-5 w-5" />
                Onboarding Video (First-Login Modal)
                {onboardingCfgLoading && (
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); fetchOnboardingCfg(); }}
                  disabled={onboardingCfgLoading}
                  data-testid="onboarding-cfg-refresh-btn"
                >
                  <RefreshCw className={`h-4 w-4 ${onboardingCfgLoading ? 'animate-spin' : ''}`} />
                </Button>
                {onboardingCfgExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Controls the welcome video shown to users on first login. Edit the URL or disable globally.
            </p>
          </CardHeader>

          {onboardingCfgExpanded && (
            <CardContent className="space-y-4">
              {/* Enable toggle */}
              <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <Checkbox
                  id="onboardingEnabled"
                  checked={!!onboardingCfg.enabled}
                  onCheckedChange={(checked) => {
                    setOnboardingCfg(prev => ({ ...prev, enabled: !!checked }));
                    setOnboardingCfgDirty(true);
                  }}
                  data-testid="onboarding-cfg-enabled-checkbox"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label
                    htmlFor="onboardingEnabled"
                    className="text-sm font-medium text-slate-800 cursor-pointer"
                  >
                    Show onboarding video on first registration / login
                  </Label>
                  <p className="text-xs text-slate-600 mt-0.5">
                    When unchecked, the first-login welcome video modal is disabled for <strong>all</strong> users globally.
                    Individual users can still dismiss it per-account via the &ldquo;Do not show me this again&rdquo; option.
                  </p>
                </div>
              </div>

              {/* Video URL */}
              <div className="space-y-2">
                <Label htmlFor="onboardingVideoUrl" className="text-sm font-medium">
                  YouTube video URL
                </Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    id="onboardingVideoUrl"
                    type="url"
                    placeholder="https://www.youtube.com/embed/VIDEO_ID"
                    value={onboardingCfg.video_url || ''}
                    onChange={(e) => {
                      setOnboardingCfg(prev => ({ ...prev, video_url: e.target.value }));
                      setOnboardingCfgDirty(true);
                    }}
                    className="flex-1 bg-white"
                    data-testid="onboarding-cfg-video-url-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetOnboardingCfgToDefault}
                    disabled={!onboardingCfg.default_video_url}
                    data-testid="onboarding-cfg-reset-default-btn"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset to default
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Accepted formats: <code>https://www.youtube.com/watch?v=…</code>, <code>https://youtu.be/…</code>,
                  or <code>https://www.youtube.com/embed/…</code>. Non-YouTube links are stored as-is.
                </p>
              </div>

              {/* Live preview */}
              {onboardingCfg.video_url && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Preview</Label>
                  <div className="relative w-full max-w-xl bg-black rounded-lg overflow-hidden border border-slate-200" style={{ aspectRatio: '16 / 9' }}>
                    <iframe
                      src={toEmbedUrl(onboardingCfg.video_url)}
                      title="Onboarding video preview"
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                      data-testid="onboarding-cfg-preview-frame"
                    />
                  </div>
                </div>
              )}

              {/* Action bar + metadata */}
              <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-slate-200">
                <div className="text-xs text-slate-500">
                  {onboardingCfg.updated_by && (
                    <>
                      Last updated by <span className="font-medium">{onboardingCfg.updated_by}</span>
                      {onboardingCfg.updated_at && (
                        <> on {new Date(onboardingCfg.updated_at).toLocaleString()}</>
                      )}
                    </>
                  )}
                  {!onboardingCfg.updated_by && (
                    <span>Using platform defaults — no admin override yet.</span>
                  )}
                </div>
                <Button
                  onClick={saveOnboardingCfg}
                  disabled={!onboardingCfgDirty || onboardingCfgSaving}
                  className="bg-[#0891b2] hover:bg-[#0e7490] text-white"
                  data-testid="onboarding-cfg-save-btn"
                >
                  {onboardingCfgSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save settings
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Users Table */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-lg text-[#0891b2]">
                Users ({totalUsers})
                {selectedCount > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({selectedCount} selected)
                  </span>
                )}
              </CardTitle>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-64"
                    data-testid="user-search"
                  />
                </div>
                {/* Status Filter */}
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40" data-testid="status-filter">
                    <SelectValue placeholder="Filter status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="Pending">
                      <span className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-amber-500" />
                        Pending
                      </span>
                    </SelectItem>
                    <SelectItem value="Free">Free Credits</SelectItem>
                    <SelectItem value="Unlimited">Unlimited</SelectItem>
                    <SelectItem value="Locked">Locked</SelectItem>
                    <SelectItem value="Blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={exportCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={selectedCount === 0}
                  className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected ({selectedCount})
                </Button>
                <Button 
                  size="sm"
                  onClick={() => {
                    setShowDeleteAllModal(true);
                    setDeleteAllStep(1);
                    setDeleteAllConfirmText('');
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-all-btn"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Delete ALL Users
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-600 w-10">
                      <Checkbox
                        checked={selectAll}
                        onCheckedChange={handleSelectAll}
                        disabled={deletableUsersCount === 0}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">User / Email</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">
                      <button 
                        onClick={() => handleSort('created_at')}
                        className="flex items-center gap-1 hover:text-[#0891b2]"
                      >
                        Registered
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">
                      <button 
                        onClick={() => handleSort('last_login_at')}
                        className="flex items-center gap-1 hover:text-[#0891b2]"
                      >
                        Last Login
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Location</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">
                      <button 
                        onClick={() => handleSort('last_seen_at')}
                        className="flex items-center gap-1 hover:text-[#0891b2]"
                      >
                        Last Online
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">
                      <button 
                        onClick={() => handleSort('total_analyses')}
                        className="flex items-center gap-1 hover:text-[#0891b2]"
                      >
                        Analyses
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Quota</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users
                    .filter(u => filterStatus === 'all' || u.quota_status === filterStatus || (filterStatus === 'Free' && u.quota_status?.includes('Free')))
                    .map((u) => {
                    const isProtected = isProtectedUser(u.email);
                    const isSelected = selectedUsers.has(u.id);
                    const isAdmin = u.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
                    
                    return (
                      <tr 
                        key={u.id} 
                        className={`border-b border-slate-100 transition-colors ${
                          isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                        } ${u.is_blocked ? 'bg-orange-50/50' : ''} ${isProtected && !u.is_blocked ? 'bg-amber-50/50' : ''} ${u.quota_status === 'Pending' ? 'bg-amber-50' : ''}`}
                      >
                        <td className="py-3 px-2">
                          {isProtected ? (
                            <div className="w-4 h-4 flex items-center justify-center" title="Protected account">
                              <Lock className="h-3 w-3 text-amber-500" />
                            </div>
                          ) : (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => handleSelectUser(u.id, checked)}
                              data-testid={`select-user-${u.id}`}
                            />
                          )}
                        </td>
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium text-gray-800 flex items-center gap-2">
                              {u.name}
                              {u.is_blocked && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                                  <Ban className="h-3 w-3" />
                                  Blocked
                                </span>
                              )}
                              {isProtected && !u.is_blocked && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                  Protected
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-gray-600">{formatDate(u.created_at)}</td>
                        <td className="py-3 px-2 text-gray-600">{getTimeAgo(u.last_login_at)}</td>
                        <td className="py-3 px-2">
                          {u.last_login_ip ? (
                            <div>
                              <p className="text-xs text-gray-700">
                                {u.last_login_country && u.last_login_country !== 'Internal' 
                                  ? `${u.last_login_city ? u.last_login_city + ', ' : ''}${u.last_login_country}`
                                  : u.last_login_country || ''}
                              </p>
                              <p className="text-xs text-gray-400 font-mono">{u.last_login_ip}</p>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-gray-600">{getTimeAgo(u.last_seen_at)}</td>
                        <td className="py-3 px-2">
                          {u.is_blocked ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                              <Ban className="h-2 w-2" />
                              Blocked
                            </span>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              u.is_online 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              <Circle className={`h-2 w-2 ${u.is_online ? 'fill-green-500 text-green-500' : 'fill-gray-400 text-gray-400'}`} />
                              {u.is_online ? 'Online' : 'Offline'}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-gray-600 font-medium">{u.total_analyses}</td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.quota_status === 'Unlimited' 
                              ? 'bg-green-100 text-green-700'
                              : u.quota_status === 'Locked'
                              ? 'bg-red-100 text-red-700'
                              : u.quota_status === 'Blocked'
                              ? 'bg-orange-100 text-orange-700'
                              : u.quota_status === 'Pending'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {u.quota_status}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex gap-1">
                            {/* Approve/Revoke Button */}
                            {!isAdmin && !u.is_unlimited && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleApproval(u.id, u.is_approved)}
                                className={`text-xs ${
                                  u.is_approved 
                                    ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' 
                                    : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                                }`}
                                title={u.is_approved ? 'Revoke approval' : 'Approve user for 10 free analyses'}
                                data-testid={`toggle-approval-${u.id}`}
                              >
                                {u.is_approved ? (
                                  <>
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Revoke
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Approve
                                  </>
                                )}
                              </Button>
                            )}
                            {/* Block/Unblock Button */}
                            {!isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleBlock(u.id, u.is_blocked)}
                                className={`text-xs ${
                                  u.is_blocked 
                                    ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
                                    : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                                }`}
                                title={u.is_blocked ? 'Unblock this user' : 'Block this user'}
                                data-testid={`toggle-block-${u.id}`}
                              >
                                {u.is_blocked ? (
                                  <>
                                    <ShieldOff className="h-3 w-3 mr-1" />
                                    Unblock
                                  </>
                                ) : (
                                  <>
                                    <Ban className="h-3 w-3 mr-1" />
                                    Block
                                  </>
                                )}
                              </Button>
                            )}
                            {/* Reset Password Button — opens dialog and POSTs
                                /api/admin/users/reset-password. Hidden for the
                                current admin's own row to avoid foot-guns. */}
                            {!isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPasswordResetDialog(u)}
                                className="text-xs text-cyan-700 hover:text-cyan-800 hover:bg-cyan-50"
                                title={`Reset password for ${u.email}`}
                                data-testid={`reset-password-${u.id}`}
                              >
                                <KeyRound className="h-3 w-3 mr-1" />
                                Reset
                              </Button>
                            )}
                            {/* Email History Button — opens EmailHistoryModal
                                which reads /api/admin/users/{id}/email-history
                                and lets the admin resend Welcome / Approval. */}
                            {!isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEmailHistoryUser(u)}
                                className="text-xs text-indigo-700 hover:text-indigo-800 hover:bg-indigo-50"
                                title={`View email delivery history for ${u.email}`}
                                data-testid={`email-history-${u.id}`}
                              >
                                <Mail className="h-3 w-3 mr-1" />
                                Email
                              </Button>
                            )}
                          </div>
                          {isAdmin && (
                            <span className="text-xs text-gray-400">Admin</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {users.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500">
                  No users found
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Shared Analyses (admin-only tick-box per record) */}
        <SharedAnalysesPanel token={token} />

        {/* Curated "Try [Species]" Examples — admin picks the source record per
            (module × species) cell. Replaces the old hardcoded moduleExamples.js. */}
        <CuratedExamplesPanel />

        {/* Financial Settings (admin-only, read-only snapshot) */}
        <AdminFinancialPanel token={token} />
      </div>

      {/* Email History Modal — opened from per-user row "Email" action.
          Reads /api/admin/users/{id}/email-history and lets the admin
          manually re-trigger Welcome / Approval emails (with Yes/Cancel
          confirmation). Never mutates user approval state. */}
      <EmailHistoryModal
        open={!!emailHistoryUser}
        onClose={() => setEmailHistoryUser(null)}
        user={emailHistoryUser}
        token={token}
      />

      {/* Reset Password Modal — opened from per-user row Action column */}
      <Dialog
        open={!!passwordResetTarget}
        onOpenChange={(open) => { if (!open) closePasswordResetDialog(); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-700">
              <KeyRound className="h-5 w-5" />
              Reset password
            </DialogTitle>
            <DialogDescription className="pt-2">
              Set a new password for{' '}
              <span className="font-semibold text-slate-900">
                {passwordResetTarget?.email}
              </span>
              . The user will be able to log in immediately with the new value.
              Account lock counters will be cleared.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="reset-pwd-input" className="text-sm">
              New password
            </Label>
            <Input
              id="reset-pwd-input"
              type="text"
              autoComplete="new-password"
              spellCheck={false}
              value={passwordResetValue}
              onChange={(e) => setPasswordResetValue(e.target.value)}
              placeholder="Minimum 6 characters"
              data-testid="reset-password-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !passwordResetSaving) submitPasswordReset();
              }}
            />
            <p className="text-xs text-slate-500">
              Plaintext is sent over the wire once and stored only as a bcrypt
              hash. Pass it to the user via a secure channel.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={closePasswordResetDialog}
              disabled={passwordResetSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={submitPasswordReset}
              disabled={passwordResetSaving || passwordResetValue.length < 6}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="reset-password-confirm"
            >
              {passwordResetSaving ? 'Saving…' : 'Reset password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Selected Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Selected Users
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to permanently delete <strong>{selectedCount}</strong> selected user record(s)?
              <br /><br />
              <span className="text-red-600 font-medium">This action cannot be undone.</span>
              <br /><br />
              All associated data will be deleted, including:
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>Analysis history</li>
                <li>Usage metrics</li>
                <li>Quota counters</li>
                <li>Activity logs</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSelected}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : `Delete ${selectedCount} User(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Modal */}
      <Dialog open={showDeleteAllModal} onOpenChange={(open) => {
        setShowDeleteAllModal(open);
        if (!open) {
          setDeleteAllStep(1);
          setDeleteAllConfirmText('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete ALL Users
            </DialogTitle>
          </DialogHeader>
          
          {deleteAllStep === 1 ? (
            <>
              <DialogDescription className="pt-2">
                <span className="text-red-600 font-bold text-lg block mb-2">WARNING: DESTRUCTIVE ACTION</span>
                You are about to permanently delete <strong>ALL user records</strong> from the database.
                <br /><br />
                <span className="text-red-600 font-medium">This action cannot be undone.</span>
                <br /><br />
                The following accounts will be preserved:
                <ul className="list-disc list-inside mt-2 text-sm text-green-600">
                  <li>totalproductionsolutions@gmail.com (Admin)</li>
                  <li>chrisclayburn@aqua-ecosystems.com (Owner)</li>
                </ul>
                <br />
                All other users and their data will be permanently deleted.
              </DialogDescription>
              <DialogFooter className="flex gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteAllModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setDeleteAllStep(2)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  I Understand, Continue
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogDescription className="pt-2">
                <span className="text-red-600 font-bold block mb-4">FINAL CONFIRMATION REQUIRED</span>
                To proceed with deleting all users, type <strong className="font-mono bg-gray-100 px-2 py-1 rounded">DELETE ALL</strong> in the box below:
              </DialogDescription>
              <Input
                value={deleteAllConfirmText}
                onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                placeholder="Type DELETE ALL to confirm"
                className="font-mono"
                data-testid="delete-all-confirm-input"
              />
              <DialogFooter className="flex gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteAllStep(1);
                    setDeleteAllConfirmText('');
                  }}
                  disabled={deleting}
                >
                  Back
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteAll}
                  disabled={deleting || deleteAllConfirmText !== 'DELETE ALL'}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  data-testid="final-delete-all-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete ALL Users'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Model Override Confirmation Modal */}
      <Dialog open={showOverrideConfirmModal} onOpenChange={setShowOverrideConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-600">⚠️ Confirm Model Override</DialogTitle>
          </DialogHeader>
          <DialogDescription className="pt-2 space-y-3">
            <p>
              This change will affect <strong>all users</strong> and <strong>all models</strong> for:
            </p>
            <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
              <p className="font-medium text-gray-900">
                {selectedSpecies}
                {speciesWithGeneticOrigin.includes(selectedSpecies) && ` (${selectedGeneticOrigin})`}
              </p>
              <div className="mt-2 text-sm text-gray-600">
                <p>Model Adjustment: <span className="font-semibold">{modelAdjustment}%</span> 
                  <span className="text-gray-400 ml-1">(affects growth)</span>
                </p>
                <p>Daily Feed Adjustment: <span className="font-semibold">{feedAdjustment}%</span>
                  <span className="text-gray-400 ml-1">(affects feed only)</span>
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-500">
              Changes take effect immediately for all new analyses. Historical saved outputs are not affected.
            </p>
          </DialogDescription>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setShowOverrideConfirmModal(false)}
              disabled={savingOverride}
            >
              Cancel
            </Button>
            <Button
              onClick={saveModelOverride}
              disabled={savingOverride}
              className="bg-[#0891b2] hover:bg-[#0e7490]"
              data-testid="confirm-override-btn"
            >
              {savingOverride ? 'Saving...' : 'Confirm & Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Model Review Modal */}
      <Dialog open={showModelReviewModal} onOpenChange={setShowModelReviewModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0891b2]">
              <Eye className="h-5 w-5" />
              Review Custom Model
            </DialogTitle>
          </DialogHeader>
          
          {modelToReview && (
            <div className="space-y-4 py-2">
              {/* Model Details */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                    modelToReview.model_type === 'SGR' ? 'bg-cyan-100 text-cyan-700' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {modelToReview.model_type}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                    modelToReview.status === 'RECEIVED' ? 'bg-blue-100 text-blue-700' :
                    modelToReview.status === 'PRE_PROCESSED' ? 'bg-amber-100 text-amber-700' :
                    modelToReview.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    Current: {modelToReview.status}
                  </span>
                </div>
                <h4 className="font-semibold text-gray-800">{modelToReview.model_name}</h4>
                <p className="text-sm text-gray-500">Species: {modelToReview.species || 'Seabream'}</p>
                <p className="text-sm text-gray-500">User: {modelToReview.user_email}</p>
                <p className="text-sm text-gray-500">
                  Submitted: {modelToReview.submitted_at ? new Date(modelToReview.submitted_at).toLocaleString() : '-'}
                </p>
                {modelToReview.file_name && (
                  <p className="text-sm text-gray-500">
                    File: {modelToReview.file_name}
                  </p>
                )}
                {modelToReview.description && (
                  <p className="text-sm text-gray-600 pt-2 border-t border-gray-200">
                    {modelToReview.description}
                  </p>
                )}
              </div>

              {/* Status Update */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Update Status</label>
                <select
                  value={reviewStatus}
                  onChange={(e) => setReviewStatus(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md bg-white"
                  data-testid="review-status-select"
                >
                  <option value="">-- Select Status --</option>
                  <option value="RECEIVED">Received</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="APPROVED_INSTALLED">Approved & Installed</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              {/* Review Notes */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Review Notes {reviewStatus === 'REJECTED' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder={reviewStatus === 'REJECTED' ? 'Please provide reason for rejection...' : 'Optional notes for the user...'}
                  className="w-full p-2 border border-gray-300 rounded-md resize-none"
                  rows={3}
                  data-testid="review-notes-input"
                />
              </div>

              {/* Status Change Preview */}
              {reviewStatus && (
                <div className={`p-3 rounded-lg border ${
                  reviewStatus === 'APPROVED_INSTALLED' ? 'bg-green-50 border-green-200' :
                  reviewStatus === 'REJECTED' ? 'bg-red-50 border-red-200' :
                  'bg-amber-50 border-amber-200'
                }`}>
                  <p className="text-sm">
                    {reviewStatus === 'APPROVED_INSTALLED' && (
                      <span className="text-green-700">
                        <CheckCircle2 className="h-4 w-4 inline mr-1" />
                        Model will be approved and installed. It will be available for {modelToReview.user_email} to use in analyses.
                      </span>
                    )}
                    {reviewStatus === 'REJECTED' && (
                      <span className="text-red-700">
                        <XCircle className="h-4 w-4 inline mr-1" />
                        Model will be rejected. User will see the rejection notes.
                      </span>
                    )}
                    {reviewStatus === 'PROCESSING' && (
                      <span className="text-amber-700">
                        <Clock className="h-4 w-4 inline mr-1" />
                        Model status will be updated to Processing (being reviewed).
                      </span>
                    )}
                    {reviewStatus === 'RECEIVED' && (
                      <span className="text-blue-700">
                        <Upload className="h-4 w-4 inline mr-1" />
                        Model status will be reset to Received.
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowModelReviewModal(false);
                setModelToReview(null);
                setReviewStatus('');
                setReviewNotes('');
              }}
              disabled={updatingModel}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateModelStatus}
              disabled={updatingModel || !reviewStatus || (reviewStatus === 'REJECTED' && !reviewNotes.trim())}
              className={`${
                reviewStatus === 'APPROVED_INSTALLED' || reviewStatus === 'APPROVED' ? 'bg-green-600 hover:bg-green-700' :
                reviewStatus === 'REJECTED' ? 'bg-red-600 hover:bg-red-700' :
                'bg-[#0891b2] hover:bg-[#0e7490]'
              }`}
              data-testid="submit-review-btn"
            >
              {updatingModel ? 'Updating...' : `Set as ${reviewStatus || '...'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Model Assignment Modal */}
      <Dialog open={showAssignmentModal} onOpenChange={setShowAssignmentModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Settings2 className="h-5 w-5" />
              Assign Models to Client
            </DialogTitle>
            <DialogDescription>
              {assignmentClientEmail}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* SGR Model Assignment */}
            <div className="space-y-3 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
              <Label className="font-medium text-cyan-700">SGR Model Source</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="sgr-default"
                    checked={assignmentForm.sgr_model_source === 'DEFAULT'}
                    onChange={() => setAssignmentForm(prev => ({
                      ...prev,
                      sgr_model_source: 'DEFAULT',
                      sgr_custom_model_id: ''
                    }))}
                    className="h-4 w-4 text-cyan-600 rounded"
                  />
                  <label htmlFor="sgr-default" className="text-sm font-medium cursor-pointer">
                    Default TPS Model
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="sgr-custom"
                    checked={assignmentForm.sgr_model_source === 'CUSTOM'}
                    onChange={() => setAssignmentForm(prev => ({
                      ...prev,
                      sgr_model_source: 'CUSTOM'
                    }))}
                    disabled={clientApprovedModels.filter(m => m.model_type === 'SGR').length === 0}
                    className="h-4 w-4 text-cyan-600 rounded disabled:opacity-50"
                  />
                  <label htmlFor="sgr-custom" className="text-sm font-medium cursor-pointer">
                    Custom Model
                  </label>
                </div>
                {assignmentForm.sgr_model_source === 'CUSTOM' && (
                  <div className="ml-7">
                    <Select
                      value={assignmentForm.sgr_custom_model_id}
                      onValueChange={(value) => setAssignmentForm(prev => ({
                        ...prev,
                        sgr_custom_model_id: value
                      }))}
                    >
                      <SelectTrigger className="bg-white" data-testid="sgr-model-select">
                        <SelectValue placeholder="Select approved SGR model" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientApprovedModels
                          .filter(m => m.model_type === 'SGR')
                          .map(model => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.model_name} ({model.species})
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                    {clientApprovedModels.filter(m => m.model_type === 'SGR').length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No approved SGR models available</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* FCR Model Assignment */}
            <div className="space-y-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <Label className="font-medium text-emerald-700">FCR Model Source</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="fcr-default"
                    checked={assignmentForm.fcr_model_source === 'DEFAULT'}
                    onChange={() => setAssignmentForm(prev => ({
                      ...prev,
                      fcr_model_source: 'DEFAULT',
                      fcr_custom_model_id: ''
                    }))}
                    className="h-4 w-4 text-emerald-600 rounded"
                  />
                  <label htmlFor="fcr-default" className="text-sm font-medium cursor-pointer">
                    Default TPS Model
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="fcr-custom"
                    checked={assignmentForm.fcr_model_source === 'CUSTOM'}
                    onChange={() => setAssignmentForm(prev => ({
                      ...prev,
                      fcr_model_source: 'CUSTOM'
                    }))}
                    disabled={clientApprovedModels.filter(m => m.model_type === 'FCR').length === 0}
                    className="h-4 w-4 text-emerald-600 rounded disabled:opacity-50"
                  />
                  <label htmlFor="fcr-custom" className="text-sm font-medium cursor-pointer">
                    Custom Model
                  </label>
                </div>
                {assignmentForm.fcr_model_source === 'CUSTOM' && (
                  <div className="ml-7">
                    <Select
                      value={assignmentForm.fcr_custom_model_id}
                      onValueChange={(value) => setAssignmentForm(prev => ({
                        ...prev,
                        fcr_custom_model_id: value
                      }))}
                    >
                      <SelectTrigger className="bg-white" data-testid="fcr-model-select">
                        <SelectValue placeholder="Select approved FCR model" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientApprovedModels
                          .filter(m => m.model_type === 'FCR')
                          .map(model => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.model_name} ({model.species})
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                    {clientApprovedModels.filter(m => m.model_type === 'FCR').length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No approved FCR models available</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAssignmentModal(false)}
              disabled={savingAssignment}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignment}
              disabled={savingAssignment || 
                (assignmentForm.sgr_model_source === 'CUSTOM' && !assignmentForm.sgr_custom_model_id) ||
                (assignmentForm.fcr_model_source === 'CUSTOM' && !assignmentForm.fcr_custom_model_id)
              }
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-assignment-btn"
            >
              {savingAssignment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Assignment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temperature Model Review Modal */}
      <Dialog open={showTempModelReviewModal} onOpenChange={setShowTempModelReviewModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <Thermometer className="h-5 w-5" />
              Review Temperature Model
            </DialogTitle>
            <DialogDescription>
              {tempModelToReview?.name} - {tempModelToReview?.user_email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Model Info */}
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-gray-500">Model Name</p>
                  <p className="font-medium">{tempModelToReview?.name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Year Type</p>
                  <p className="font-medium">{tempModelToReview?.year_type || '365-day'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Submitted</p>
                  <p className="font-medium">
                    {tempModelToReview?.submitted_at ? new Date(tempModelToReview.submitted_at).toLocaleDateString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">File</p>
                  <p className="font-medium text-xs truncate">{tempModelToReview?.file_name || 'N/A'}</p>
                </div>
              </div>
            </div>
            
            {/* Status Selection */}
            <div className="space-y-3">
              <Label className="font-medium">Update Status</Label>
              <div className="grid grid-cols-2 gap-2">
                {['RECEIVED', 'PROCESSING', 'APPROVED_INSTALLED', 'REJECTED'].map((status) => (
                  <div 
                    key={status}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      tempReviewStatus === status 
                        ? status === 'APPROVED_INSTALLED' ? 'border-green-500 bg-green-50' :
                          status === 'REJECTED' ? 'border-red-500 bg-red-50' :
                          status === 'PROCESSING' ? 'border-amber-500 bg-amber-50' :
                          'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setTempReviewStatus(status)}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox checked={tempReviewStatus === status} />
                      <span className="text-sm font-medium">
                        {status === 'APPROVED_INSTALLED' ? 'Approved & Installed' : status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {status === 'RECEIVED' && 'Model received, awaiting review'}
                      {status === 'PROCESSING' && 'Under review by TPS team'}
                      {status === 'APPROVED_INSTALLED' && 'Available for user analyses'}
                      {status === 'REJECTED' && 'Model rejected, notify user'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Review Notes */}
            <div className="space-y-2">
              <Label>Review Notes (Optional)</Label>
              <Input
                placeholder="Add notes about this review..."
                value={tempReviewNotes}
                onChange={(e) => setTempReviewNotes(e.target.value)}
              />
            </div>
            
            {/* Available to All Users Checkbox */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="is-public-checkbox"
                  checked={tempIsPublic}
                  onCheckedChange={(checked) => setTempIsPublic(checked)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <label htmlFor="is-public-checkbox" className="text-sm font-medium text-blue-800 cursor-pointer">
                    Available to All Users
                  </label>
                  <p className="text-xs text-blue-600 mt-1">
                    If checked, this temperature model will be available for all users to select in their analyses. 
                    If unchecked, only the uploader can use it.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowTempModelReviewModal(false);
                setTempModelToReview(null);
              }}
              disabled={updatingTempModel}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateTempModelStatus}
              disabled={updatingTempModel || !tempReviewStatus}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="update-temp-status-btn"
            >
              {updatingTempModel ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Status'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add FCR Version Modal */}
      <Dialog open={showAddVersionModal} onOpenChange={setShowAddVersionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Percent className="h-5 w-5" />
              Add New Version
            </DialogTitle>
            <DialogDescription>
              Create a new version for {fcrOrigins.find(o => o.code === addVersionOriginCode)?.name || addVersionOriginCode} origin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Version Name</Label>
              <Input
                placeholder="e.g., v2 - Winter Adjusted"
                value={newVersionName}
                onChange={(e) => setNewVersionName(e.target.value)}
                data-testid="new-version-name-input"
              />
              <p className="text-xs text-gray-500">Leave blank for auto-generated name (v2, v3, etc.)</p>
            </div>
            
            <div className="space-y-2">
              <Label>Multiplier Percentage</Label>
              <div className="flex items-center gap-3">
                <Slider
                  value={[newVersionMultiplier]}
                  onValueChange={(vals) => setNewVersionMultiplier(vals[0])}
                  min={50}
                  max={150}
                  step={1}
                  className="flex-1"
                />
                <div className="flex items-center gap-1 w-24">
                  <Input
                    type="number"
                    min="50"
                    max="150"
                    value={newVersionMultiplier}
                    onChange={(e) => setNewVersionMultiplier(Math.min(150, Math.max(50, parseFloat(e.target.value) || 100)))}
                    className="h-8 text-center"
                    data-testid="new-version-multiplier-input"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                50% = -50% FCR, 100% = No change, 150% = +50% FCR
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="Notes about this version..."
                value={newVersionNotes}
                onChange={(e) => setNewVersionNotes(e.target.value)}
                data-testid="new-version-notes-input"
              />
            </div>
            
            {/* Preview */}
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-700">
                <strong>Preview:</strong> If base FCR = 1.50, then Final FCR = {(1.50 * newVersionMultiplier / 100).toFixed(3)}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddVersionModal(false);
                setAddVersionOriginCode('');
                setNewVersionName('');
                setNewVersionMultiplier(100);
                setNewVersionNotes('');
              }}
              disabled={savingVersion}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddFcrVersion}
              disabled={savingVersion || newVersionMultiplier < 50 || newVersionMultiplier > 150}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-version-btn"
            >
              {savingVersion ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Version'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
